import React, { useState, useEffect, useRef, useMemo, useCallback, startTransition } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDatabase } from '../context/DatabaseContext';
import { useToast } from '../context/ToastContext';
import { ConfirmModal } from '../components/ui/ConfirmModal';
import { Card } from '../components/ui/Card';
import { DateRangePicker } from '../components/ui/DateRangePicker';
import {
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableHeadCell,
    TableCell,
    TableHeaderRow,
} from '../components/ui/Table';
import {
    FileText,
    RefreshCw,
    AlertCircle,
    Loader,
    ArrowLeft,
    Lightbulb,
    Shield,
    Zap,
    ArrowRight,
    Trash2,
    Plus,
    Settings,
    Hash,
    ChevronLeft,
    ChevronRight,
    Clock,
    ChevronDown,
    ChevronUp,
    X,
    PanelRight,
    Archive,
    LayoutDashboard,
    GitBranch,
    MessageCircle,
    Search,
    Pencil,
    Check,
    Calendar,
    Sparkles,
} from 'lucide-react';
import {
    createReportGeneration,
    executeReportGeneration,
    getReportsForDatabase,
    syncReport,
    getReportGeneration,
    updateReportGeneration,
    getGeneratedReportUIManifest,
    deleteReportGeneration,
    deleteTimeFrameReport,
    listPlans,
    generatePlans,
    updatePlan,
    getAllQueries,
    updateReportPlan,
    updateReportTimeFramePlans,
} from '../services/api';
import { formatLocalDate, formatLocalTimeOnly } from '../utils/timezone';
import {
    clearImplementations,
    isCompletedImplementation,
    loadImplementations,
} from '../utils/reportPlanImplementations';
import { ReportRecommendations } from '../components/reports/ReportRecommendations';

import {
    GeneratedReportDashboard,
    GeneratedOverallDashboard,
    hasGeneratedOverallDashboard,
    getOverallDashboardStatus,
    topicHasGeneratedDashboard,
} from '../reports/reportDashboardRegistry.generated';
import { useSuppressWebpackErrorOverlay } from '../hooks/useSuppressWebpackErrorOverlay';
import { PlanUsedSkillsTags } from '../components/reports/PlanUsedSkillsTags';

const REPORT_TIME_FRAME_SEGMENTS = [
    { id: 'yesterday', label: 'Yesterday' },
    { id: 'week_to_date', label: 'Week to Date' },
    { id: 'last_week', label: 'Last Week' },
    { id: 'month_to_date', label: 'Month to Date' },
    { id: 'last_month', label: 'Last Month' },
];

// Time-frame keys still used by the master-plan editor (per-frame query lists).
const REPORT_TIME_FRAME_KEYS_FE = REPORT_TIME_FRAME_SEGMENTS.map((s) => s.id);

/**
 * Poll GET /api/reports/{id} until `isDone(reportData)` is true, then resolve with that
 * report row. The backend now kicks off master planning / execution as a background task
 * and returns immediately, so the client drives the "generating" spinner off this poll
 * instead of a single long-lived request that a proxy/gateway can time out (which produced
 * the false "Failed to create/execute report generation" toast while the backend kept
 * working). Transient network errors are swallowed — the next tick retries.
 */
async function pollReportUntil(reportId, isDone, { intervalMs = 2000, timeoutMs = 15 * 60 * 1000 } = {}) {
    const maxTicks = Math.max(1, Math.ceil(timeoutMs / intervalMs));
    for (let i = 0; i < maxTicks; i++) {
        try {
            const res = await getReportGeneration(reportId);
            const data = res.data?.data;
            if (data && isDone(data)) return data;
        } catch {
            /* transient blip — keep polling; backend may still be working */
        }
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
    throw new Error('Report generation timed out. It may still be processing — refresh shortly.');
}


/**
 * Textarea that grows to fit its content (no inner scrollbar). Resizes on every value
 * change and on mount, so it fits both typed input and pre-loaded saved text.
 */
const AutoResizeTextarea = ({ value, className = '', minHeight = 72, ...props }) => {
    const ref = useRef(null);
    const resize = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.max(el.scrollHeight, minHeight)}px`;
    }, [minHeight]);
    useEffect(() => {
        resize();
    }, [value, resize]);
    return (
        <textarea
            ref={ref}
            value={value}
            onInput={resize}
            style={{ minHeight, overflow: 'hidden' }}
            className={`resize-none ${className}`}
            {...props}
        />
    );
};

/**
 * Lowercase the keys of a single result row so generated dashboards (which always
 * read lowercase snake_case keys, e.g. `r.category`) match rows regardless of the
 * casing the DB driver reported. MSSQL/MySQL/ClickHouse and Postgres quoted aliases
 * preserve original case, so `AS Category` would otherwise arrive as `Category` and
 * the component's `r.category` would fall back to 'Unknown'. On a case collision the
 * first non-nullish value wins.
 */
function lowercaseRowKeys(row) {
    if (!row || typeof row !== 'object' || Array.isArray(row)) return row;
    const out = {};
    for (const [key, value] of Object.entries(row)) {
        const lk = typeof key === 'string' ? key.toLowerCase() : key;
        if (lk in out && (value == null || value === '')) continue;
        out[lk] = value;
    }
    return out;
}

function normalizeExecutedQueries(executedQueries) {
    if (!Array.isArray(executedQueries)) return [];
    return executedQueries.map((q) => {
        if (!q || typeof q !== 'object') return q;
        let rows = q.result;
        if (typeof rows === 'string') {
            try {
                rows = JSON.parse(rows);
            } catch {
                rows = [];
            }
        }
        if (rows != null && !Array.isArray(rows)) rows = [];
        rows = Array.isArray(rows) ? rows.map(lowercaseRowKeys) : [];
        return { ...q, result: rows };
    });
}


/** Prefer newest ReportTimeFrame row per type (version / timestamps). Backend sorts too; this keeps UI correct if order differs. */
function pickLatestTimeFrameReport(reports) {
    if (!Array.isArray(reports) || reports.length === 0) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    // Distance (ms) of a run's covered range from today — the run whose date
    // range ends closest to today wins, so anchored re-runs default to the most
    // current one. Runs without a parseable date fall to the back.
    const proximity = (r) => {
        const iso = r?.date_range?.end_date || r?.date_range?.start_date;
        const d = iso ? new Date(`${iso}T00:00:00`) : null;
        if (!d || Number.isNaN(d.getTime())) return Infinity;
        return Math.abs(d.getTime() - today.getTime());
    };
    const sorted = [...reports].sort((a, b) => {
        const pa = proximity(a);
        const pb = proximity(b);
        if (pa !== pb) return pa - pb; // closest to today first
        // Same range → newest version, then newest write.
        const va = Number(a.version) || 0;
        const vb = Number(b.version) || 0;
        if (vb !== va) return vb - va;
        const ta = String(a.updated_at || a.created_at || '');
        const tb = String(b.updated_at || b.created_at || '');
        return tb.localeCompare(ta);
    });
    return sorted[0];
}

function isNonDateParameterName(paramName, sampleValue) {
    const name = String(paramName).toLowerCase();
    if (name.includes('date')) return false;
    if (
        sampleValue != null &&
        typeof sampleValue === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(sampleValue)
    ) {
        return false;
    }
    return true;
}

/**
 * Map for Configure Parameters: start from master_plan.parameters_needed defaults,
 * then overlay values from the latest executed_queries snapshot (per query.parameters).
 */
function buildParameterValuesForReport(data) {
    const initialValues = {};
    const parametersNeeded = data?.master_plan?.parameters_needed || {};
    Object.entries(parametersNeeded).forEach(([queryId, queryParams]) => {
        Object.entries(queryParams).forEach(([paramName, defaultValue]) => {
            if (isNonDateParameterName(paramName, defaultValue)) {
                initialValues[`${queryId}_${paramName}`] = defaultValue;
            }
        });
    });

    const byType = data?.time_frame_reports_by_type;
    if (!byType || typeof byType !== 'object') return initialValues;

    const tfOrder = ['last_week', 'yesterday', 'week_to_date', 'month_to_date', 'last_month'];
    for (const tf of tfOrder) {
        const reports = byType[tf];
        if (!reports?.length) continue;
        const latest = pickLatestTimeFrameReport(reports);
        const rawEq = latest?.executed_queries;
        if (!Array.isArray(rawEq) || rawEq.length === 0) continue;

        const eq = normalizeExecutedQueries(rawEq);
        eq.forEach((q) => {
            const qid = q.query_id;
            const params = q.parameters;
            if (qid == null || !params || typeof params !== 'object') return;
            Object.entries(params).forEach(([paramName, value]) => {
                if (!isNonDateParameterName(paramName, value)) return;
                const key = `${qid}_${paramName}`;
                if (value !== undefined && value !== null) {
                    initialValues[key] = value;
                }
            });
        });
        break;
    }

    return initialValues;
}

/** Keys are `{queryId}_{paramName}` where paramName may contain underscores (e.g. min_occurrence). */
function parseParameterStorageKey(key) {
    if (typeof key !== 'string' || !key) return null;
    const i = key.indexOf('_');
    if (i <= 0 || i >= key.length - 1) return null;
    const queryId = key.slice(0, i);
    const paramName = key.slice(i + 1);
    if (!/^\d+$/.test(queryId) || !paramName) return null;
    return { queryId, paramName };
}

function formatCompactInteger(n) {
    const abs = Math.abs(n);
    const sign = n < 0 ? '-' : '';
    if (abs >= 1_000_000_000)
        return `${sign}${(abs / 1_000_000_000).toFixed(1).replace(/\.0$/, '')}B`;
    if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
    if (abs >= 1_000) return `${sign}${(abs / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
    return String(n);
}

function formatExecutedQueryDisplayCell(value) {
    if (value == null) return { text: 'NULL', muted: true };
    if (typeof value === 'boolean') return { text: value ? 'true' : 'false', muted: false };
    if (typeof value === 'number' && Number.isFinite(value)) {
        if (Number.isInteger(value) && Math.abs(value) >= 1000)
            return { text: formatCompactInteger(value), muted: false };
        if (!Number.isInteger(value)) {
            const s = Number(value).toLocaleString(undefined, { maximumFractionDigits: 6 });
            return { text: s, muted: false };
        }
        return { text: String(value), muted: false };
    }
    if (typeof value === 'object') {
        try {
            return { text: JSON.stringify(value), muted: false };
        } catch {
            return { text: '[object]', muted: false };
        }
    }
    const str = String(value);
    if (str.trim() !== '' && /^-?\d+(\.\d+)?$/.test(str.trim())) {
        const num = Number(str);
        if (Number.isInteger(num) && Math.abs(num) >= 1000)
            return { text: formatCompactInteger(num), muted: false };
    }
    return { text: str, muted: false };
}

function getExecutedQueryPreviewRows(query) {
    return Array.isArray(query?.result) ? query.result.slice(0, 10) : [];
}

function getExecutedQueryColumns(rows) {
    if (!Array.isArray(rows) || rows.length === 0) return [];
    const columns = [];
    rows.forEach((row) => {
        if (!row || typeof row !== 'object' || Array.isArray(row)) return;
        Object.keys(row).forEach((key) => {
            if (!columns.includes(key)) columns.push(key);
        });
    });
    return columns;
}

function ExecutedQueryPreviewCard({ query, index }) {
    const previewRows = getExecutedQueryPreviewRows(query);
    const columns = getExecutedQueryColumns(previewRows);
    const totalRows = Number(query?.row_count) || (Array.isArray(query?.result) ? query.result.length : 0);
    const queryTitle = query?.query_name?.trim() || `Query ${index + 1}`;
    const isSuccess = query?.status === 'success';

    return (
        <Card padding="none" className="rounded-2xl overflow-hidden shadow-[0_1px_3px_rgba(0,0,0,0.06),0_1px_2px_rgba(0,0,0,0.04)]">
            <div className="px-6 pt-5 pb-4">
                <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0 pr-2">
                        <h4 className="text-base font-semibold text-gray-900 tracking-tight">{queryTitle}</h4>
                        <p className="text-xs text-gray-400 mt-1.5 font-normal">
                            {totalRows} rows
                            {query?.execution_time_ms != null ? ` • ${query.execution_time_ms} ms` : ''}
                        </p>
                    </div>
                    <span
                        className={`shrink-0 inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ${isSuccess
                            ? 'bg-emerald-50 text-emerald-800 ring-1 ring-inset ring-emerald-600/15'
                            : query?.status === 'error'
                                ? 'bg-red-50 text-red-800 ring-1 ring-inset ring-red-600/15'
                                : 'bg-gray-50 text-gray-700 ring-1 ring-inset ring-gray-200'
                            }`}
                    >
                        {query?.status || 'unknown'}
                    </span>
                </div>
            </div>

            {!isSuccess ? (
                <div className="mx-6 mb-5 rounded-lg border border-red-100 bg-red-50/80 px-4 py-3 text-sm text-red-800">
                    {query?.error_message || 'This query did not execute successfully.'}
                </div>
            ) : columns.length === 0 ? (
                <div className="px-6 pb-8 pt-2 text-left text-sm text-gray-400">This query returned no rows.</div>
            ) : (
                <>
                    <Table variant="default">
                        <TableHead>
                            <TableHeaderRow>
                                {columns.map((column) => (
                                    <TableHeadCell key={column} align="left">
                                        {column.replace(/_/g, ' ')}
                                    </TableHeadCell>
                                ))}
                            </TableHeaderRow>
                        </TableHead>
                        <TableBody>
                            {previewRows.map((row, rowIdx) => (
                                <TableRow key={`${query?.id || query?.query_id || index}-${rowIdx}`}>
                                    {columns.map((column) => {
                                        const rawValue = row?.[column];
                                        const { text, muted } = formatExecutedQueryDisplayCell(rawValue);
                                        return (
                                            <TableCell
                                                key={`${column}-${rowIdx}`}
                                                align="left"
                                                className="align-top font-semibold text-gray-900 break-words whitespace-normal leading-snug"
                                            >
                                                {rawValue == null || muted ? (
                                                    <span className="font-medium italic text-gray-400">{text}</span>
                                                ) : (
                                                    text
                                                )}
                                            </TableCell>
                                        );
                                    })}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    <div className="px-6 pb-5 pt-3 text-left text-xs text-gray-400">
                        Showing {previewRows.length} of {totalRows} rows
                    </div>
                </>
            )}
        </Card>
    );
}

export const ReportGeneration = () => {
    const { t } = useTranslation(['page-report-generation', 'common']);
    const { databaseId } = useParams();
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { databases } = useDatabase();
    const { showToast } = useToast();

    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    const [topic, setTopic] = useState('');
    const [reportDescription, setReportDescription] = useState('');
    const [showMasterPlan, setShowMasterPlan] = useState(false);
    const [showMasterPlanDetails, setShowMasterPlanDetails] = useState(false);
    const [masterPlanData, setMasterPlanData] = useState(null);
    const [executing, setExecuting] = useState(false);
    const [updatingPlan, setUpdatingPlan] = useState(false);
    const [addQueryPickerFor, setAddQueryPickerFor] = useState(null);
    const [addQuerySearch, setAddQuerySearch] = useState('');
    // Pre-generation plan edits: per-frame rationale drafts + additional-notes draft.
    // A draft of `undefined`/`null` means "not editing — show the saved value".
    const [frameRationaleDrafts, setFrameRationaleDrafts] = useState({});
    const [additionalNotesDraft, setAdditionalNotesDraft] = useState(null);
    // Which inline save is in flight: 'notes' | `frame:<tf>` | null (for the right spinner).
    const [planEditSaveTarget, setPlanEditSaveTarget] = useState(null);
    // Master "Edit plan" toggle — gates add/remove queries, rationale, and notes edits.
    const [planEditMode, setPlanEditMode] = useState(false);
    const [implementedRecommendations, setImplementedRecommendations] = useState([]);
    const [partialUpdateModalOpen, setPartialUpdateModalOpen] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [selectedTimeFrame, setSelectedTimeFrame] = useState(null);
    const selectedTimeFrameRef = useRef(null);
    const [reportsList, setReportsList] = useState([]);
    const [viewingReport, setViewingReport] = useState(false);
    const [parameterValues, setParameterValues] = useState({});
    const [deleteModalInfo, setDeleteModalInfo] = useState({ isOpen: false, id: null });
    const [deletingReport, setDeletingReport] = useState(false);
    // Per-time-frame version delete (a single generated run of one time frame).
    const [deleteVersionInfo, setDeleteVersionInfo] = useState({ isOpen: false, timeFrameId: null, label: '', timeFrameType: null });
    const [deletingVersion, setDeletingVersion] = useState(false);
    const [plans, setPlans] = useState([]);
    const [generatingPlans, setGeneratingPlans] = useState(false);
    const [plansRefreshToken, setPlansRefreshToken] = useState(0);
    // Custom date-range editor for the active time frame (re-run with these dates).
    const [dateEditOpen, setDateEditOpen] = useState(false);
    // "Saved runs" dropdown — browse previously generated frames without re-running.
    const [savedRunsOpen, setSavedRunsOpen] = useState(false);
    // Versions dropdown — jump to any past run of the active frame (not just ◀ ▶).
    const [versionsOpen, setVersionsOpen] = useState(false);
    const [dateDraft, setDateDraft] = useState({ start_date: '', end_date: '' });
    // Global anchor ("as of" date) — re-runs all frames relative to it.
    const [anchorDraft, setAnchorDraft] = useState('');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showRightSidebar, setShowRightSidebar] = useState(true);
    const [includeArchivedPlans, setIncludeArchivedPlans] = useState(false);
    const [inSettings, setInSettings] = useState(false);
    const [reportNameDraft, setReportNameDraft] = useState('');
    const [savingReportName, setSavingReportName] = useState(false);
    const [generatedUiManifest, setGeneratedUiManifest] = useState(null);
    /** Latest manifest for poll guard — avoids exhaustive-deps on manifest while not restarting the interval each poll. */
    const generatedUiManifestRef = useRef(generatedUiManifest);
    generatedUiManifestRef.current = generatedUiManifest;
    const [overallReports, setOverallReports] = useState([]);
    const [overallLoading, setOverallLoading] = useState(false);
    const [overallRefreshToken, setOverallRefreshToken] = useState(0);
    const [executedQueriesExpanded, setExecutedQueriesExpanded] = useState(false);
    const descriptionRef = useRef(null);

    useEffect(() => {
        if (showCreateModal && descriptionRef.current) {
            descriptionRef.current.style.height = 'auto';
            descriptionRef.current.style.height = `${descriptionRef.current.scrollHeight}px`;
        }
    }, [reportDescription, showCreateModal]);


    // Query mapping for names and details
    const [databaseQueriesMap, setDatabaseQueriesMap] = useState({});
    const [queryDetailModalInfo, setQueryDetailModalInfo] = useState({ isOpen: false, queryId: null });

    const refreshQueriesMap = useCallback(() => {
        if (!databaseId) return Promise.resolve();
        return getAllQueries(databaseId)
            .then((response) => {
                const queries = response.data?.data?.queries || [];
                const qMap = {};
                queries.forEach((q) => {
                    qMap[q.id] = q;
                });
                setDatabaseQueriesMap(qMap);
            })
            .catch((err) => console.error('Failed to fetch queries:', err));
    }, [databaseId]);

    useEffect(() => {
        refreshQueriesMap();
    }, [refreshQueriesMap]);

    const handleImplementationsChange = useCallback((items) => {
        setImplementedRecommendations(items);
    }, []);

    useEffect(() => {
        const reportId = reportData?.id || reportData?.report_generation_id;
        if (!reportId || !showMasterPlanDetails) return;
        const reload = () => setImplementedRecommendations(loadImplementations(reportId));
        reload();
        window.addEventListener('focus', reload);
        return () => window.removeEventListener('focus', reload);
    }, [reportData?.id, reportData?.report_generation_id, showMasterPlanDetails]);

    const runUpdatePlan = async () => {
        const reportId = reportData?.id || reportData?.report_generation_id;
        const completedItems = implementedRecommendations.filter(isCompletedImplementation);
        if (!reportId || !completedItems.length) {
            showToast(t('toasts.finishRecommendationBeforeUpdate'), 'error');
            return;
        }
        setUpdatingPlan(true);
        try {
            const response = await updateReportPlan(reportId, {
                implemented_recommendations: completedItems.map((item) => ({
                    index: item.index,
                    recommendation: item.recommendation,
                    outcome: item.outcome,
                })),
            });
            const newPlan = response.data?.data?.master_plan;
            if (!newPlan) {
                showToast(t('toasts.planUpdateReturnedNoMasterPlan'), 'error');
                return;
            }
            setMasterPlanData(newPlan);
            clearImplementations(reportId);
            setImplementedRecommendations([]);
            setReportData((prev) => ({
                ...prev,
                master_plan: newPlan,
                status: response.data?.data?.status ?? prev?.status,
            }));
            // Re-derive Configure Parameters from the freshly returned plan only.
            // Do NOT overlay the previous values or the stale executed_queries from
            // the pre-update reportData closure — both would keep showing old
            // parameters/defaults after the plan changes.
            setParameterValues(buildParameterValuesForReport({ master_plan: newPlan }));
            await refreshQueriesMap();
            showToast(t('toasts.reportPlanUpdated'), 'success');
        } catch (error) {
            showToast(
                error.response?.data?.detail || t('toasts.failedToUpdateReportPlan'),
                'error'
            );
        } finally {
            setUpdatingPlan(false);
            setPartialUpdateModalOpen(false);
        }
    };

    /**
     * Persist a new `time_frame_plans` map (query ids per time frame) before generation.
     * Mirrors runUpdatePlan: refresh masterPlanData / reportData and re-derive Configure
     * Parameters from the freshly returned plan so the params section follows the queries.
     */
    const persistTimeFramePlans = async (newTimeFramePlans, additionalNotes) => {
        const reportId = reportData?.id || reportData?.report_generation_id;
        if (!reportId) return;
        setUpdatingPlan(true);
        try {
            const payload = { time_frame_plans: newTimeFramePlans };
            // Only send notes when an edit is in flight — omitting leaves them untouched.
            if (additionalNotes !== undefined) payload.additional_notes = additionalNotes;
            const response = await updateReportTimeFramePlans(reportId, payload);
            const newPlan = response.data?.data?.master_plan;
            if (!newPlan) {
                showToast(t('toasts.planUpdateReturnedNoMasterPlan'), 'error');
                return;
            }
            setMasterPlanData(newPlan);
            setReportData((prev) => ({
                ...prev,
                master_plan: newPlan,
                status: response.data?.data?.status ?? prev?.status,
            }));
            setParameterValues(buildParameterValuesForReport({ master_plan: newPlan }));
        } catch (error) {
            showToast(
                error.response?.data?.detail || t('toasts.failedToUpdateTimeFramePlans'),
                'error'
            );
        } finally {
            setUpdatingPlan(false);
        }
    };

    /** Snapshot current query_ids for every frame, then apply `mutate` to one frame's ids. */
    const buildFramesWithChange = (targetTf, mutate) => {
        const frames = masterPlanData?.time_frame_plans || {};
        const next = {};
        REPORT_TIME_FRAME_KEYS_FE.forEach((tf) => {
            const ids = Array.isArray(frames[tf]?.query_ids) ? frames[tf].query_ids.map(Number) : [];
            next[tf] = { query_ids: tf === targetTf ? mutate(ids) : ids };
        });
        return next;
    };

    /** Add a query to a single time frame only. */
    const handleAddQueryToPlan = async (queryId, tfKey) => {
        const qid = Number(queryId);
        if (!Number.isFinite(qid) || !tfKey) return;
        const next = buildFramesWithChange(tfKey, (ids) =>
            ids.includes(qid) ? ids : [...ids, qid]
        );
        setAddQueryPickerFor(null);
        await persistTimeFramePlans(next);
    };

    /** Remove a query from a single time frame only. */
    const handleRemoveQueryFromPlan = async (queryId, tfKey) => {
        const qid = Number(queryId);
        if (!Number.isFinite(qid) || !tfKey) return;
        const next = buildFramesWithChange(tfKey, (ids) => ids.filter((id) => id !== qid));
        await persistTimeFramePlans(next);
    };

    /** Save the edited rationale (description) for a single time frame. */
    const handleSaveFrameRationale = async (tfKey) => {
        const draft = frameRationaleDrafts[tfKey];
        if (draft === undefined) return; // not editing
        // Snapshot all frames' query_ids unchanged, then attach the edited rationale.
        const next = buildFramesWithChange(tfKey, (ids) => ids);
        next[tfKey] = { ...next[tfKey], rationale: draft };
        setPlanEditSaveTarget(`frame:${tfKey}`);
        await persistTimeFramePlans(next);
        setFrameRationaleDrafts((prev) => {
            const copy = { ...prev };
            delete copy[tfKey];
            return copy;
        });
        setPlanEditSaveTarget(null);
    };

    /** Save the edited Additional Notes for the master plan. */
    const handleSaveAdditionalNotes = async () => {
        if (additionalNotesDraft === null) return; // not editing
        // Snapshot every frame's query_ids unchanged; only the notes change.
        const next = buildFramesWithChange(REPORT_TIME_FRAME_KEYS_FE[0], (ids) => ids);
        setPlanEditSaveTarget('notes');
        await persistTimeFramePlans(next, additionalNotesDraft);
        setAdditionalNotesDraft(null);
        setPlanEditSaveTarget(null);
    };

    const handleUpdatePlanClick = () => {
        const completedItems = implementedRecommendations.filter(isCompletedImplementation);
        if (!completedItems.length) {
            showToast(t('toasts.finishRecommendationBeforeUpdate'), 'error');
            return;
        }
        const recs = masterPlanData?.recommendations || [];
        const allDone =
            recs.length > 0
            && recs.every((_, idx) => completedItems.some((item) => item.index === idx));
        if (!allDone) {
            setPartialUpdateModalOpen(true);
            return;
        }
        runUpdatePlan();
    };

    // Topic preset options
    const topicPresets = [
        t('topicPresets.revenueData'),
        t('topicPresets.customerAnalysis'),
        t('topicPresets.marketTrends'),
        t('topicPresets.salesFunnel'),
        t('topicPresets.productMetrics'),
        t('topicPresets.leadReport'),
        t('topicPresets.regionalSales'),
        t('topicPresets.salesSummary'),
        t('topicPresets.retentionStats'),
    ];

    const database = databases.find(db => db.id === parseInt(databaseId));

    useEffect(() => {
        selectedTimeFrameRef.current = selectedTimeFrame;
    }, [selectedTimeFrame]);

    const liveExecutedQueries = useMemo(
        () => normalizeExecutedQueries(selectedTimeFrame?.executed_queries),
        [selectedTimeFrame?.executed_queries],
    );

    const reportTopicNorm = useMemo(
        () => reportData?.topic?.trim().toLowerCase().replace(/_/g, ' ') || '',
        [reportData?.topic],
    );

    /**
     * Manifest is scoped to the database (all topics), not a single report. Clear it only when
     * switching databases — clearing on every report id change caused a race: the poll effect ran
     * with the previous manifest (topic looked ready → no polling), then this effect nulled the
     * manifest, leaving the UI stuck on "Building report dashboard" until a full reload.
     */
    useEffect(() => {
        setGeneratedUiManifest(null);
    }, [databaseId]);

    /** Poll live manifest + report row until Claude codegen registers the dashboard or terminal state. */
    useEffect(() => {
        if (!viewingReport || reportData?.status !== 'completed' || !reportData?.id || !reportTopicNorm) {
            return undefined;
        }
        if (topicHasGeneratedDashboard(reportTopicNorm, generatedUiManifestRef.current)) {
            return undefined;
        }

        const reportId = reportData.id;
        let intervalId;
        let cancelled = false;

        async function tick() {
            if (cancelled) return;
            try {
                const mRes = await getGeneratedReportUIManifest(databaseId);
                const manifest = mRes.data?.data ?? { topics: {} };
                setGeneratedUiManifest(manifest);
                const manifestReady = topicHasGeneratedDashboard(reportTopicNorm, manifest);
                // Always refresh the report row. The backend writes the manifest BEFORE flipping
                // codegen status off 'in_progress' (two separate commits), so stopping on
                // manifestReady alone can leave codegenStatus stale at 'in_progress' →
                // dashboardReady stays false and the loading overlay sticks until a manual reload.
                const rRes = await getReportGeneration(reportId);
                const d = rRes.data?.data;
                if (d) setReportData(d);
                const cg = d?.user_parameters?.report_dashboard_codegen;
                if (cg === 'failed' || cg === 'skipped') {
                    if (intervalId) clearInterval(intervalId);
                    return;
                }
                // Stop only once the dashboard is registered AND codegen has left 'in_progress'.
                if (manifestReady && cg !== 'in_progress') {
                    if (intervalId) clearInterval(intervalId);
                    return;
                }
            } catch {
                /* network blip — next tick retries */
            }
        }

        tick();
        intervalId = setInterval(tick, 2000);
        return () => {
            cancelled = true;
            if (intervalId) clearInterval(intervalId);
        };
    }, [viewingReport, reportData?.status, reportData?.id, reportTopicNorm, databaseId]);

    const codegenStatus = reportData?.user_parameters?.report_dashboard_codegen;
    const codegenError = reportData?.user_parameters?.report_dashboard_codegen_error;
    const topicManifestReady = topicHasGeneratedDashboard(reportTopicNorm, generatedUiManifest);
    const dashboardReady =
        topicManifestReady && codegenStatus !== 'in_progress';
    const overallDashboardReady = hasGeneratedOverallDashboard(databaseId, generatedUiManifest);
    const overallStatus = getOverallDashboardStatus(databaseId, generatedUiManifest);
    /** Use list endpoint — do not wait for heavy per-report payloads; enables manifest polling and UI paint immediately. */
    const hasAnyCompletedReport = useMemo(
        () => reportsList.some((r) => r.status === 'completed'),
        [reportsList],
    );

    const suppressCodegenOverlay = useMemo(() => {
        if (viewingReport && liveExecutedQueries?.length > 0) {
            if (codegenStatus === 'failed' || codegenStatus === 'skipped') return false;
            if (codegenStatus === 'in_progress') return true;
            if (!dashboardReady) return true;
        }

        if (!viewingReport && !showMasterPlan && hasAnyCompletedReport) {
            if (overallStatus.status === 'failed') return false;
            if (overallStatus.status === 'in_progress') return true;
            if (!overallDashboardReady) return true;
        }

        return false;
    }, [
        viewingReport,
        showMasterPlan,
        liveExecutedQueries?.length,
        codegenStatus,
        dashboardReady,
        hasAnyCompletedReport,
        overallStatus.status,
        overallDashboardReady,
    ]);

    useSuppressWebpackErrorOverlay(suppressCodegenOverlay);

    /** Load manifest as soon as the overall landing view is shown so codegen status is visible without blocking on full report fetches. */
    useEffect(() => {
        if (!databaseId || viewingReport || showMasterPlan) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const mRes = await getGeneratedReportUIManifest(databaseId);
                if (cancelled) return;
                const manifest = mRes.data?.data ?? { topics: {}, overall: {} };
                setGeneratedUiManifest(manifest);
            } catch {
                /* non-fatal */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [databaseId, viewingReport, showMasterPlan]);

    /**
     * Poll the manifest while waiting for the overall dashboard to register (or overall codegen to fail).
     */
    useEffect(() => {
        if (viewingReport || showMasterPlan || !databaseId) return undefined;
        if (!hasAnyCompletedReport) return undefined;
        if (overallDashboardReady) return undefined;
        if (overallStatus.status === 'failed') return undefined;

        let intervalId;
        let cancelled = false;

        async function tick() {
            if (cancelled) return;
            try {
                const mRes = await getGeneratedReportUIManifest(databaseId);
                const manifest = mRes.data?.data ?? { topics: {}, overall: {} };
                if (hasGeneratedOverallDashboard(databaseId, manifest)) {
                    setGeneratedUiManifest(manifest);
                    if (intervalId) clearInterval(intervalId);
                    return;
                }
                const status = getOverallDashboardStatus(databaseId, manifest);
                if (status.status === 'failed') {
                    setGeneratedUiManifest(manifest);
                    if (intervalId) clearInterval(intervalId);
                    return;
                }
            } catch {
                /* network blip — next tick retries */
            }
        }

        tick();
        intervalId = setInterval(tick, 2000);
        return () => {
            cancelled = true;
            if (intervalId) clearInterval(intervalId);
        };
    }, [viewingReport, showMasterPlan, databaseId, hasAnyCompletedReport, overallDashboardReady, overallStatus.status]);
    const showExecutedQueriesPanel = useMemo(
        () =>
            dashboardReady ||
            codegenStatus === 'failed' ||
            codegenStatus === 'skipped',
        [dashboardReady, codegenStatus],
    );

    const sortedTimeFrameReports = (reportsByType) => {
        if (!reportsByType) return [];
        const firstReports = Object.values(reportsByType)
            .map((reports) => pickLatestTimeFrameReport(reports))
            .filter(Boolean);
        return firstReports.sort((a, b) => {
            const orderA = ['yesterday', 'week_to_date', 'last_week', 'month_to_date', 'last_month'].indexOf(a.time_frame_type);
            const orderB = ['yesterday', 'week_to_date', 'last_week', 'month_to_date', 'last_month'].indexOf(b.time_frame_type);
            return orderA - orderB;
        });
    };

    // Auto-select the most recently generated frame when viewing a completed report
    // without a selected time frame (keeps parity with fetchReportDetails).
    useEffect(() => {
        if (viewingReport && reportData?.status === 'completed' && reportData?.time_frame_reports_by_type && !selectedTimeFrame) {
            const byType = reportData.time_frame_reports_by_type;
            let bestType = null;
            let bestWhen = -Infinity;
            for (const [type, rows] of Object.entries(byType)) {
                if (!rows?.length) continue;
                const latest = pickLatestTimeFrameReport(rows);
                const when = latest?.created_at ? new Date(latest.created_at).getTime() : 0;
                if (when >= bestWhen) {
                    bestWhen = when;
                    bestType = type;
                }
            }
            const rows = (bestType && byType[bestType]) || byType['custom'] || byType['last_week'];
            const latest = pickLatestTimeFrameReport(rows);
            if (latest && rows?.length) {
                setSelectedTimeFrame({ ...latest, allReports: rows });
            }
        }
    }, [viewingReport, reportData, selectedTimeFrame]); // eslint-disable-line react-hooks/exhaustive-deps



    // Persist current report in URL so page reload stays on the same report
    useEffect(() => {
        if (!databaseId) return;
        if (viewingReport && reportData?.id) {
            const params = new URLSearchParams();
            params.set('view', String(reportData.id));
            if (selectedTimeFrame?.time_frame_type) {
                params.set('timeFrame', selectedTimeFrame.time_frame_type);
            }
            navigate(`/reports/${databaseId}?${params.toString()}`, { replace: true });
        }
    }, [viewingReport, reportData?.id, selectedTimeFrame?.time_frame_type]); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchReportsList = React.useCallback(async () => {
        try {
            const response = await getReportsForDatabase(databaseId);
            const reports = response.data?.data?.reports || [];
            setReportsList(reports);
        } catch (error) {
            console.error('Failed to fetch reports list:', error);
        }
    }, [databaseId]);

    const loadOverallSnapshot = React.useCallback(async () => {
        if (!databaseId) return;
        const ids = reportsList.filter((r) => r.status === 'completed').map((r) => r.id);
        if (ids.length === 0) {
            startTransition(() => {
                setOverallReports([]);
                setOverallLoading(false);
            });
            return;
        }
        setOverallLoading(true);
        try {
            const payloads = [];
            for (const id of ids) {
                const r = await getReportGeneration(id);
                const data = r.data?.data;
                if (data) payloads.push(data);
                await new Promise((resolve) => {
                    requestAnimationFrame(() => resolve());
                });
            }
            startTransition(() => {
                setOverallReports(payloads);
                setOverallLoading(false);
            });
        } catch (error) {
            console.error('Failed to load overall dashboard data:', error);
            startTransition(() => {
                setOverallReports([]);
                setOverallLoading(false);
            });
        }
    }, [databaseId, reportsList]);

    useEffect(() => {
        if (viewingReport || showMasterPlan || !databaseId) {
            return;
        }
        loadOverallSnapshot();
    }, [
        viewingReport,
        showMasterPlan,
        databaseId,
        overallRefreshToken,
        loadOverallSnapshot,
    ]);

    useEffect(() => {
        if (databaseId) {
            fetchReportsList();

            // Check if we're viewing a specific report from URL
            const viewReportId = searchParams.get('view');
            const timeFrame = searchParams.get('timeFrame');

            if (viewReportId) {
                // If the report in URL is different from what's currently loaded, fetch it
                if (String(reportData?.id) !== viewReportId) {
                    fetchReportDetails(viewReportId, timeFrame);
                }
                setShowRightSidebar(false);
            } else {
                // If no report in URL, we are on the main page
                setReportData(null);
                setViewingReport(false);
                setSelectedTimeFrame(null);
                setShowMasterPlan(false);
                setShowRightSidebar(true);
            }
        }
    }, [databaseId, searchParams, fetchReportsList]); // eslint-disable-line react-hooks/exhaustive-deps

    /** Same reset as landing with no `view` search param — needed because navigate() is a no-op when already on that path. */
    const goToOverallPerformanceLanding = useCallback(() => {
        setInSettings(false);
        setShowMasterPlan(false);
        setShowMasterPlanDetails(false);
        setMasterPlanData(null);
        setViewingReport(false);
        setSelectedTimeFrame(null);
        setReportData(null);
        setParameterValues({});
        setShowRightSidebar(true);
        navigate(`/reports/${databaseId}`, { replace: true });
    }, [databaseId, navigate]);

    useEffect(() => {
        if (selectedTimeFrame?.id) {
            listPlans({
                timeFrameReportId: selectedTimeFrame.id,
                status: includeArchivedPlans ? 'all' : undefined,
            })
                .then((response) => {
                    setPlans(response.data?.data || []);
                })
                .catch((error) => {
                    console.error('Failed to fetch plans:', error);
                    setPlans([]);
                });
        } else {
            setPlans([]);
        }
    }, [selectedTimeFrame?.id, includeArchivedPlans, plansRefreshToken]);

    // Seed the custom date-range draft from the active frame — but NOT while the editor
    // is open (a live tab-switch must not clobber the dates the user is typing).
    useEffect(() => {
        if (dateEditOpen) return;
        const dr = selectedTimeFrame?.date_range || {};
        setDateDraft({
            start_date: dr.start_date || '',
            end_date: dr.end_date || '',
        });
    }, [selectedTimeFrame?.id]); // eslint-disable-line react-hooks/exhaustive-deps

    // Seed the draft from the active frame each time the editor is opened.
    useEffect(() => {
        if (!dateEditOpen) return;
        const dr = selectedTimeFrameRef.current?.date_range || {};
        setDateDraft({
            start_date: dr.start_date || '',
            end_date: dr.end_date || '',
        });
    }, [dateEditOpen]);

    // Seed the anchor draft from the report's stored reference_date.
    useEffect(() => {
        setAnchorDraft(reportData?.reference_date || '');
    }, [reportData?.id, reportData?.reference_date]);

    useEffect(() => {
        // Seed the name draft whenever the editable name field becomes visible — in the
        // settings pane OR when the "Edit plan" toggle is on.
        if ((inSettings || planEditMode) && reportData?.topic != null) {
            setReportNameDraft(reportData.topic);
        }
    }, [inSettings, planEditMode, reportData?.id, reportData?.topic]);

    const handleSaveReportName = async () => {
        const name = reportNameDraft.trim();
        if (!reportData?.id) {
            showToast(t('toasts.noReportSelected'), 'error');
            return;
        }
        if (!name) {
            showToast(t('toasts.reportNameCannotBeEmpty'), 'error');
            return;
        }
        if (name === (reportData.topic || '').trim()) {
            return;
        }

        setSavingReportName(true);
        try {
            const response = await updateReportGeneration(reportData.id, { topic: name });
            const updated = response.data?.data;
            const newTopic = updated?.topic ?? name;
            setReportData((prev) => (prev ? { ...prev, topic: newTopic } : prev));
            setTopic(newTopic);
            await fetchReportsList();
            setGeneratedUiManifest(null);
            showToast(response.data?.message || t('toasts.reportNameUpdated'), 'success');
        } catch (error) {
            console.error('Failed to update report name:', error);
            showToast(
                error.response?.data?.detail || t('toasts.failedToUpdateReportName'),
                'error',
            );
        } finally {
            setSavingReportName(false);
        }
    };

    const handleSetPlanStatus = async (planId, nextStatus) => {
        try {
            await updatePlan(planId, { status: nextStatus });
            showToast(nextStatus === 'archived' ? t('toasts.planArchived') : t('toasts.planUnarchived'), 'success');

            if (selectedTimeFrame?.id) {
                const response = await listPlans({
                    timeFrameReportId: selectedTimeFrame.id,
                    status: includeArchivedPlans ? 'all' : undefined,
                });
                setPlans(response.data?.data || []);
            }
        } catch (error) {
            console.error('Failed to update plan status:', error);
            showToast(t('toasts.failedToUpdatePlanStatus'), 'error');
        }
    };

    const handleCreateReport = async () => {
        if (!topic.trim()) {
            showToast(t('toasts.pleaseEnterReportTopic'), 'error');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                database_id: parseInt(databaseId),
                topic: topic,
            };
            const desc = reportDescription.trim();
            if (desc) {
                payload.description = desc;
            }
            // POST returns immediately with a report id; master planning runs in the
            // background. Keep the spinner and poll until the plan is ready (status leaves
            // "planning") or it fails.
            const response = await createReportGeneration(payload);
            const created = response.data?.data;
            const reportId = created?.id || created?.report_generation_id;
            if (!reportId) {
                throw new Error('Report generation did not start (no id returned)');
            }

            const data = await pollReportUntil(reportId, (d) => d.status !== 'planning');
            if (data.status === 'failed') {
                throw new Error('Report generation plan failed. Please try again.');
            }

            setReportData(data);
            setShowMasterPlan(true);
            setViewingReport(false);

            // Show master plan details view with recommendations
            if (data?.master_plan) {
                setMasterPlanData(data.master_plan);
                setShowMasterPlanDetails(true);
                if (data.id) clearImplementations(data.id);
                setImplementedRecommendations([]);
                setParameterValues(buildParameterValuesForReport(data));
            }

            showToast(t('toasts.reportPlanCreatedSuccessfully'), 'success');
            setShowCreateModal(false);
            setReportDescription('');
        } catch (error) {
            console.error('Failed to create report generation:', error);
            showToast(
                error.response?.data?.detail || error.message || t('toasts.failedToCreateReportGeneration'),
                'error'
            );
        } finally {
            setLoading(false);
        }
    };

    const handleExecuteWithParameters = async () => {
        const reportId = reportData?.id || reportData?.report_generation_id;

        if (!reportId) {
            showToast('No report generation to execute', 'error');
            return;
        }

        // Build parameters by query from the parameter values
        const parametersByQuery = {};
        Object.entries(parameterValues).forEach(([key, value]) => {
            const parsed = parseParameterStorageKey(key);
            if (!parsed) return;
            const { queryId, paramName } = parsed;
            if (value === undefined || value === null || value === '') return;
            if (!parametersByQuery[queryId]) {
                parametersByQuery[queryId] = {};
            }
            const n = typeof value === 'string' && value.trim() !== '' && /^-?\d+(\.\d+)?$/.test(value.trim())
                ? Number(value.trim())
                : value;
            parametersByQuery[queryId][paramName] = Number.isNaN(n) ? value : n;
        });

        setExecuting(true);
        try {
            // POST returns immediately; query execution + report generation runs in the
            // background. Keep the spinner and poll until a terminal status.
            await executeReportGeneration({
                report_generation_id: reportId,
                parameters_by_query: Object.keys(parametersByQuery).length > 0 ? parametersByQuery : undefined,
            });

            const data = await pollReportUntil(reportId, (d) =>
                ['completed', 'partial', 'failed'].includes(d.status)
            );
            if (data.status === 'failed') {
                throw new Error('Report generation failed. Please try again.');
            }

            showToast(
                data.status === 'partial'
                    ? 'Reports generated (some time frames failed).'
                    : 'Reports generated successfully',
                data.status === 'partial' ? 'warning' : 'success'
            );

            await fetchReportDetails(reportId);
            await fetchReportsList();
            setOverallRefreshToken((t) => t + 1);
        } catch (error) {
            console.error('Failed to execute report generation:', error);
            showToast(
                error.response?.data?.detail || error.message || 'Failed to execute report generation',
                'error'
            );
        } finally {
            setExecuting(false);
        }
    };

    // Re-run ALL time frames anchored to a new reference date (shifts every frame at
    // once). Use when the default period has no data — point it at a date with data.
    const handleReExecuteWithAnchor = async (referenceDate) => {
        const reportId = reportData?.id || reportData?.report_generation_id;
        if (!reportId) {
            showToast('No report to re-run', 'error');
            return;
        }
        const ref = (referenceDate || '').trim();
        if (!ref) {
            showToast('Pick an anchor date', 'error');
            return;
        }
        setExecuting(true);
        try {
            // POST returns immediately; the re-run executes in the background. Poll until
            // a terminal status so the spinner reflects real progress.
            await executeReportGeneration({
                report_generation_id: reportId,
                reference_date: ref,
            });
            const data = await pollReportUntil(reportId, (d) =>
                ['completed', 'partial', 'failed'].includes(d.status)
            );
            if (data.status === 'failed') {
                throw new Error('Report re-run failed. Please try again.');
            }
            showToast(
                data.status === 'partial'
                    ? 'Reports re-run (some time frames failed).'
                    : 'Reports re-run for the new period',
                data.status === 'partial' ? 'warning' : 'success'
            );
            setDateEditOpen(false);
            await fetchReportDetails(reportId);
            await fetchReportsList();
            setOverallRefreshToken((t) => t + 1);
        } catch (error) {
            console.error('Failed to re-run report:', error);
            showToast(
                error.response?.data?.detail || error.message || 'Failed to re-run report',
                'error'
            );
        } finally {
            setExecuting(false);
        }
    };

    const handleSyncReport = async (timeFrameType = null, dateRange = null) => {
        if (!reportData?.id) {
            showToast('No report to sync', 'error');
            return;
        }

        setSyncing(true);
        try {
            const response = await syncReport({
                report_generation_id: reportData.id,
                time_frame_type: timeFrameType,
                date_range: dateRange || undefined,
            });

            const data = response.data?.data;
            showToast(data?.message || 'Reports synced successfully', 'success');

            // The backend may route a "custom" pick to a canonical frame (exact date
            // match) — navigate to whatever type it actually wrote, not what we sent.
            const resolvedType = data?.time_frames_synced?.[0] || timeFrameType;
            await fetchReportDetails(data?.report_generation_id || reportData.id, resolvedType);
            setOverallRefreshToken((t) => t + 1);
        } catch (error) {
            console.error('Failed to sync report:', error);
            showToast(
                error.response?.data?.detail || 'Failed to sync reports',
                'error'
            );
        } finally {
            setSyncing(false);
        }
    };

    // Run the report for a user-picked custom date range. Accepts explicit dates
    // (from the DateRangePicker's Apply) or falls back to the inline dateDraft.
    const handleApplyCustomDates = async (startArg, endArg, timeFrameType = null) => {
        const start = (startArg ?? dateDraft.start_date ?? '').trim();
        const end = (endArg ?? dateDraft.end_date ?? '').trim();
        if (!start || !end) {
            showToast('Pick both a start and end date', 'error');
            return;
        }
        if (start > end) {
            showToast('Start date must be on or before end date', 'error');
            return;
        }
        setDateDraft({ start_date: start, end_date: end });
        setDateEditOpen(false);
        // A semantic preset (Yesterday, Week to Date, …) syncs by its time frame so
        // the backend computes canonical dates for the active anchor. A manual
        // calendar pick has no type and runs as a literal "custom" range.
        if (timeFrameType) {
            await handleSyncReport(timeFrameType);
        } else {
            await handleSyncReport('custom', { start_date: start, end_date: end });
        }
    };

    // Generate (or regenerate) Recommended Priorities for the active time frame.
    const handleGeneratePriorities = async (timeFrameType) => {
        if (!reportData?.id || !timeFrameType) {
            showToast('No time frame selected', 'error');
            return;
        }
        setGeneratingPlans(true);
        try {
            const response = await generatePlans({
                report_generation_id: reportData.id,
                time_frame_type: timeFrameType,
            });
            const data = response.data?.data;
            showToast(data?.message || 'Recommended priorities generated', 'success');
            setPlansRefreshToken((t) => t + 1);
        } catch (error) {
            console.error('Failed to generate priorities:', error);
            showToast(
                error.response?.data?.detail || 'Failed to generate priorities',
                'error'
            );
        } finally {
            setGeneratingPlans(false);
        }
    };

    const fetchReportDetails = async (reportId, timeFrameType, onSuccess = null, skipViewReport = false) => {
        try {
            const response = await getReportGeneration(reportId);
            const data = response.data?.data;
            const wasSameReport =
                reportData?.id != null && String(reportData.id) === String(reportId);
            const explicitTf =
                timeFrameType != null && String(timeFrameType).trim() !== ''
                    ? String(timeFrameType).trim()
                    : null;
            // Honor an explicit type (tab/version nav) or, when re-fetching the same
            // report, keep the frame the user was on. Otherwise pick nothing yet — we
            // fall through to the most-recently-generated frame below.
            const preferredType =
                explicitTf ||
                (wasSameReport ? selectedTimeFrameRef.current?.time_frame_type : null);

            setReportData(data);

            // Auto-select time frame when report is completed — always bind latest row + normalized queries from API
            if (data?.status === 'completed' && data?.time_frame_reports_by_type && !skipViewReport) {
                setViewingReport(true);
                setShowRightSidebar(false);

                const byType = data.time_frame_reports_by_type;
                let timeFrameReports = preferredType ? byType[preferredType] : null;
                if (!timeFrameReports?.length) {
                    // No preference (or that frame has no rows) → default to Last Week
                    // when it exists; otherwise land on the frame generated most
                    // recently across every type (custom or otherwise).
                    if (byType['last_week']?.length) {
                        timeFrameReports = byType['last_week'];
                    } else {
                        let bestType = null;
                        let bestWhen = -Infinity;
                        for (const [type, rows] of Object.entries(byType)) {
                            if (!rows?.length) continue;
                            const latest = pickLatestTimeFrameReport(rows);
                            const when = latest?.created_at ? new Date(latest.created_at).getTime() : 0;
                            if (when >= bestWhen) {
                                bestWhen = when;
                                bestType = type;
                            }
                        }
                        timeFrameReports = (bestType && byType[bestType]) || byType['custom'];
                    }
                }
                if (timeFrameReports?.length) {
                    const latest = pickLatestTimeFrameReport(timeFrameReports);
                    if (latest) {
                        setSelectedTimeFrame({ ...latest, allReports: timeFrameReports });
                    }
                }
            } else if (data?.status === 'analyzing' && data?.master_plan) {
                // Show master plan details view for analyzing status
                setViewingReport(false);
                setShowMasterPlan(true);
                setMasterPlanData(data.master_plan);
                setShowMasterPlanDetails(true);
            } else if (!skipViewReport) {
                // For other statuses (failed, etc.), set viewing report
                setViewingReport(true);
            }

            if (data?.master_plan) {
                setParameterValues(buildParameterValuesForReport(data));
            } else {
                setParameterValues({});
            }

            // Call onSuccess callback with fetched data if provided
            if (onSuccess) {
                onSuccess(data);
            }
        } catch (error) {
            console.error('Failed to fetch report details:', error);
        }
    };

    const handleDeleteReport = async () => {
        const reportId = deleteModalInfo.id;
        if (!reportId) {
            showToast('No report selected for deletion', 'error');
            return;
        }

        try {
            setDeletingReport(true);
            await deleteReportGeneration(reportId);
            setDeleteModalInfo({ isOpen: false, id: null });
            showToast('Report deleted successfully', 'success');

            // Reset state if we're viewing the deleted report
            if (viewingReport && reportData?.id === reportId) {
                setViewingReport(false);
                setReportData(null);
                setSelectedTimeFrame(null);
            }

            // Refresh the reports list
            await fetchReportsList();
            setOverallRefreshToken((t) => t + 1);
        } catch (error) {
            console.error('Failed to delete report:', error);
            showToast(
                error.response?.data?.detail || 'Failed to delete report',
                'error'
            );
        } finally {
            setDeletingReport(false);
        }
    };

    // Delete a single generated run (version) of one time frame, leaving the others.
    const handleDeleteTimeFrameVersion = async () => {
        const reportId = reportData?.id || reportData?.report_generation_id;
        const timeFrameId = deleteVersionInfo.timeFrameId;
        if (!reportId || !timeFrameId) {
            showToast('No report version selected for deletion', 'error');
            return;
        }
        try {
            setDeletingVersion(true);
            await deleteTimeFrameReport(reportId, timeFrameId);
            setDeleteVersionInfo({ isOpen: false, timeFrameId: null, label: '', timeFrameType: null });
            showToast('Report version deleted', 'success');
            // Reload the report so the version dropdown + active selection reflect the deletion.
            await fetchReportDetails(reportId, deleteVersionInfo.timeFrameType || undefined);
            setOverallRefreshToken((t) => t + 1);
        } catch (error) {
            console.error('Failed to delete report version:', error);
            showToast(
                error.response?.data?.detail || 'Failed to delete report version',
                'error'
            );
        } finally {
            setDeletingVersion(false);
        }
    };

    const renderMasterPlanDetails = () => {
        if (!masterPlanData) return null;

        const {
            topic_analysis,
            selected_insights = [],
            time_frame_plans = {},
            parameters_needed = {},
            recommendations = [],
            additional_notes
        } = masterPlanData;

        const completedImplementations = implementedRecommendations.filter(
            isCompletedImplementation
        );
        const hasCompletedImplementations = completedImplementations.length > 0;
        const allRecommendationsCompleted =
            recommendations.length > 0
            && recommendations.every((_, idx) =>
                completedImplementations.some((item) => item.index === idx)
            );

        // Get all time frames
        const timeFrameKeys = ['yesterday', 'week_to_date', 'last_week', 'month_to_date', 'last_month'];
        const timeFrameLabels = {
            yesterday: 'Yesterday',
            week_to_date: 'Week to Date',
            last_week: 'Last Week',
            month_to_date: 'Month to Date',
            last_month: 'Last Month',
        };

        // Count queries per time frame
        const totalQueries = Object.values(time_frame_plans).reduce((sum, tf) =>
            sum + (tf?.query_ids?.length || 0), 0
        );

        // The "Edit" toggle is offered for any persisted report (plan view AND settings pane).
        // What it unlocks depends on status:
        //   • status 'analyzing' (pre-generation) → full edit: queries, rationale, notes, name.
        //   • already generated/processed → name only (the plan is frozen once it has run).
        const planEditButtonAllowed = !!reportData;
        const planFullyEditable =
            planEditButtonAllowed && reportData?.status === 'analyzing' && planEditMode;
        const nameEditable = planEditButtonAllowed && planEditMode;
        const sortedDbQueries = Object.values(databaseQueriesMap)
            .filter(Boolean)
            .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
        // Queries available to add to a given frame = DB queries not already in THAT frame.
        const availableQueriesForFrame = (tfKey) => {
            const inFrame = new Set((time_frame_plans[tfKey]?.query_ids || []).map(Number));
            return sortedDbQueries.filter((q) => !inFrame.has(Number(q.id)));
        };

        return (
            <div>
                <div className="page-content">
                    {/* Header */}
                    <div className="mb-8">
                        <div className="flex items-start justify-between mb-6">
                            <div className="flex-1 pr-8">
                                {nameEditable ? (
                                    <div className="max-w-xl">
                                        <label
                                            htmlFor="report-settings-plan-name"
                                            className="block text-sm font-medium text-gray-700 mb-1.5"
                                        >
                                            Report name
                                        </label>
                                        <div className="flex flex-wrap items-center gap-2">
                                            <input
                                                id="report-settings-plan-name"
                                                type="text"
                                                value={reportNameDraft}
                                                onChange={(e) => setReportNameDraft(e.target.value)}
                                                onKeyDown={(e) => {
                                                    if (e.key === 'Enter') {
                                                        e.preventDefault();
                                                        handleSaveReportName();
                                                    }
                                                }}
                                                disabled={savingReportName}
                                                className="flex-1 min-w-[200px] px-4 py-2.5 text-lg font-semibold text-gray-900 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 disabled:opacity-50"
                                                placeholder="Report name"
                                            />
                                            <button
                                                type="button"
                                                onClick={handleSaveReportName}
                                                disabled={
                                                    savingReportName
                                                    || !reportNameDraft.trim()
                                                    || reportNameDraft.trim() === (reportData?.topic || '').trim()
                                                }
                                                className="px-4 py-2.5 text-sm font-medium rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {savingReportName ? 'Saving…' : 'Save name'}
                                            </button>
                                        </div>
                                        <p className="text-gray-500 mt-2 text-sm">
                                            Review the plan and configure parameters before execution
                                        </p>
                                    </div>
                                ) : (
                                    <>
                                        <h2 className="text-3xl font-bold text-gray-900 tracking-tight">
                                            {reportData?.topic || topic || 'Report Generation Plan'}
                                        </h2>
                                        <p className="text-gray-500 mt-1">
                                            Review the AI-generated plan and configure parameters before execution
                                        </p>
                                    </>
                                )}
                            </div>
                            <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
                                {planEditButtonAllowed && (
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (planEditMode) {
                                                // Leaving edit mode — discard any unsaved drafts.
                                                setFrameRationaleDrafts({});
                                                setAdditionalNotesDraft(null);
                                                setAddQueryPickerFor(null);
                                            }
                                            setPlanEditMode((v) => !v);
                                        }}
                                        disabled={updatingPlan || executing}
                                        title={
                                            planEditMode
                                                ? 'Finish editing'
                                                : reportData?.status === 'analyzing'
                                                    ? 'Edit time frames, queries, notes, and name'
                                                    : 'Edit the report name'
                                        }
                                        className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium shadow-sm whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed ${
                                            planEditMode
                                                ? 'bg-primary-600 text-white hover:bg-primary-700'
                                                : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                                        }`}
                                    >
                                        {planEditMode ? (
                                            <>
                                                <Check className="w-4 h-4" />
                                                <span>Done editing</span>
                                            </>
                                        ) : (
                                            <>
                                                <Pencil className="w-4 h-4" />
                                                <span>Edit plan</span>
                                            </>
                                        )}
                                    </button>
                                )}
                                {recommendations.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={handleUpdatePlanClick}
                                        disabled={!hasCompletedImplementations || updatingPlan || executing}
                                        title={
                                            hasCompletedImplementations
                                                ? allRecommendationsCompleted
                                                    ? 'Merge completed recommendations into the plan'
                                                    : 'Update with completed items only'
                                                : 'Finish at least one recommendation first'
                                        }
                                        className="inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-gray-300 bg-white text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm whitespace-nowrap"
                                    >
                                        {updatingPlan ? (
                                            <>
                                                <Loader className="w-4 h-4 animate-spin" />
                                                <span>Updating…</span>
                                            </>
                                        ) : (
                                            <>
                                                <RefreshCw className="w-4 h-4" />
                                                <span>Update plan</span>
                                            </>
                                        )}
                                    </button>
                                )}
                                <button
                                    onClick={handleExecuteWithParameters}
                                    disabled={executing || updatingPlan}
                                    className="inline-flex items-center justify-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-sm hover:shadow-md transition-all whitespace-nowrap"
                                >
                                    {executing ? (
                                        <>
                                            <Loader className="w-4 h-4 animate-spin" />
                                            <span>Executing...</span>
                                        </>
                                    ) : (
                                        <>
                                            <Zap className="w-4 h-4" />
                                            <span>Execute Report Generation</span>
                                        </>
                                    )}
                                </button>
                            </div>

                        </div>

                        {reportData?.description ? (
                            <Card padding="md" className="mb-8 rounded-2xl">
                                <p className="text-base text-gray-700 leading-relaxed whitespace-pre-wrap">{reportData.description}</p>
                            </Card>
                        ) : null}


                        {/* Summary Stats */}
                        <div className="grid grid-cols-4 gap-4">
                            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl border border-primary-200 p-4">
                                <p className="text-sm font-medium text-primary-700 mb-1">Selected Insights</p>
                                <p className="text-3xl font-bold text-primary-900">{selected_insights.length}</p>
                            </div>
                            <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200 p-4">
                                <p className="text-sm font-medium text-green-700 mb-1">Total Queries</p>
                                <p className="text-3xl font-bold text-green-900">{totalQueries}</p>
                            </div>
                            <div className="bg-gradient-to-br from-primary-50 to-primary-100 rounded-xl border border-primary-200 p-4">
                                <p className="text-sm font-medium text-primary-700 mb-1">Time Frames</p>
                                <p className="text-3xl font-bold text-primary-900">{Object.keys(time_frame_plans).length}</p>
                            </div>
                            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl border border-orange-200 p-4">
                                <p className="text-sm font-medium text-orange-700 mb-1">Recommendations</p>
                                <p className="text-3xl font-bold text-orange-900">{recommendations.length}</p>
                            </div>
                        </div>
                    </div>

                    {/* Topic Analysis */}
                    {topic_analysis && (
                        <Card padding="md" className="rounded-xl mb-6">
                            <div className="flex items-start gap-3">
                                <div className="w-8 h-8 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                                    <Lightbulb className="w-4 h-4 text-primary-600" />
                                </div>
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Topic Analysis</h3>
                                    <p className="text-sm text-gray-600 leading-relaxed">{topic_analysis}</p>
                                </div>
                            </div>
                        </Card>
                    )}

                    {/* Selected Insights */}
                    {selected_insights.length > 0 && (
                        <Card padding="md" className="rounded-xl mb-6" title="Selected Insights">
                            <div className="space-y-3">
                                {selected_insights.map((insight, idx) => (
                                    <div key={idx} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                        <div className="flex items-start gap-3">
                                            <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                                                <span className="text-xs font-medium text-primary-700">{idx + 1}</span>
                                            </div>
                                            <div>
                                                <p className="font-medium text-gray-900">{insight.insight_name}</p>
                                                <p className="text-sm text-gray-600 mt-1">{insight.relevance_reason}</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </Card>
                    )}

                    {/* Time Frame Plans */}
                    <Card padding="md" className="rounded-xl mb-6" title="Time Frame Plans">
                        <div className="time-frame-plans-section grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-5 lg:gap-6 items-start">
                            {timeFrameKeys.map(key => {
                                const plan = time_frame_plans[key];
                                if (!plan) return null;
                                const frameOptions = planFullyEditable ? availableQueriesForFrame(key) : [];
                                return (
                                    <div key={key} className="p-5 bg-gray-50 rounded-lg border border-gray-200">
                                        <p className="font-medium text-gray-900 mb-3">{timeFrameLabels[key]}</p>
                                        {planFullyEditable ? (() => {
                                            const savedRationale = plan.rationale || '';
                                            const draft = frameRationaleDrafts[key];
                                            const value = draft ?? savedRationale;
                                            const dirty = draft !== undefined && draft !== savedRationale;
                                            const saving = planEditSaveTarget === `frame:${key}`;
                                            return (
                                                <div className="mb-5">
                                                    <AutoResizeTextarea
                                                        value={value}
                                                        onChange={(e) => setFrameRationaleDrafts((prev) => ({
                                                            ...prev,
                                                            [key]: e.target.value,
                                                        }))}
                                                        disabled={updatingPlan}
                                                        placeholder={`Why these queries run for ${timeFrameLabels[key]}…`}
                                                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                                                    />
                                                    {dirty && (
                                                        <div className="mt-2 flex items-center justify-end gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => setFrameRationaleDrafts((prev) => {
                                                                    const copy = { ...prev };
                                                                    delete copy[key];
                                                                    return copy;
                                                                })}
                                                                disabled={updatingPlan}
                                                                className="rounded-md px-2.5 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleSaveFrameRationale(key)}
                                                                disabled={updatingPlan}
                                                                className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                                                            >
                                                                {saving && <Loader className="h-3 w-3 animate-spin" />}
                                                                {saving ? 'Saving…' : 'Save'}
                                                            </button>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })() : (
                                            <p className="text-sm text-gray-600 leading-relaxed mb-5">{plan.rationale}</p>
                                        )}
                                        <div className="flex flex-col gap-3">
                                            {plan.query_ids?.map((qid, queryIdx) => {
                                                const queryName = databaseQueriesMap[qid]?.name || `Query ${qid}`;
                                                const openQueryDetail = () =>
                                                    setQueryDetailModalInfo({ isOpen: true, queryId: qid });
                                                return (
                                                    <div
                                                        key={`${key}-${qid}-${queryIdx}`}
                                                        role="button"
                                                        tabIndex={0}
                                                        onClick={openQueryDetail}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter' || e.key === ' ') {
                                                                e.preventDefault();
                                                                openQueryDetail();
                                                            }
                                                        }}
                                                        className="time-frame-query-card group w-full cursor-pointer rounded-lg border border-gray-200 bg-white p-3 shadow-sm transition-all hover:border-primary-300 hover:bg-primary-50 flex items-start gap-2.5 text-left"
                                                    >
                                                        <Hash className="w-4 h-4 text-gray-400 shrink-0 mt-0.5 group-hover:text-primary-500" />
                                                        <span className="min-w-0 flex-1 text-xs font-medium text-gray-700 leading-relaxed break-words">
                                                            {queryName}
                                                        </span>
                                                        {planFullyEditable && (
                                                            <button
                                                                type="button"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    handleRemoveQueryFromPlan(qid, key);
                                                                }}
                                                                disabled={updatingPlan}
                                                                className="shrink-0 rounded p-0.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-500 disabled:opacity-50"
                                                                title={`Remove this query from ${timeFrameLabels[key]}`}
                                                                aria-label={`Remove ${queryName} from ${timeFrameLabels[key]}`}
                                                            >
                                                                <X className="w-3.5 h-3.5" />
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                            {planFullyEditable && plan.query_ids?.length === 0 && (
                                                <p className="text-xs italic text-gray-400">No queries selected.</p>
                                            )}
                                            {planFullyEditable && (
                                                <div className="relative">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setAddQuerySearch('');
                                                            setAddQueryPickerFor((cur) => (cur === key ? null : key));
                                                        }}
                                                        disabled={updatingPlan || frameOptions.length === 0}
                                                        className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-primary-300 bg-white px-3 py-2 text-xs font-medium text-primary-700 transition-colors hover:bg-primary-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                                        title={frameOptions.length === 0 ? 'All queries already added to this time frame' : `Add a query to ${timeFrameLabels[key]}`}
                                                    >
                                                        <Plus className="w-3.5 h-3.5" />
                                                        Add Query
                                                    </button>
                                                    {addQueryPickerFor === key && frameOptions.length > 0 && (() => {
                                                        const term = addQuerySearch.trim().toLowerCase();
                                                        const filtered = term
                                                            ? frameOptions.filter((q) =>
                                                                String(q.name || `Query ${q.id}`)
                                                                    .toLowerCase()
                                                                    .includes(term)
                                                            )
                                                            : frameOptions;
                                                        return (
                                                            <div className={`absolute z-30 mt-2 w-72 max-w-[calc(100vw-3rem)] rounded-lg border border-gray-200 bg-white shadow-xl ${['month_to_date', 'last_month'].includes(key) ? 'right-0' : 'left-0'}`}>
                                                                <div className="sticky top-0 border-b border-gray-100 bg-white p-2">
                                                                    <div className="relative">
                                                                        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
                                                                        <input
                                                                            type="text"
                                                                            autoFocus
                                                                            value={addQuerySearch}
                                                                            onChange={(e) => setAddQuerySearch(e.target.value)}
                                                                            placeholder="Search queries by name…"
                                                                            className="w-full rounded-md border border-gray-300 py-1.5 pl-8 pr-2 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                                        />
                                                                    </div>
                                                                </div>
                                                                <div className="max-h-60 space-y-1 overflow-auto p-2">
                                                                    {filtered.length === 0 ? (
                                                                        <p className="px-2 py-3 text-center text-xs italic text-gray-400">
                                                                            No queries match “{addQuerySearch.trim()}”.
                                                                        </p>
                                                                    ) : (
                                                                        filtered.map((q) => (
                                                                            <button
                                                                                key={q.id}
                                                                                type="button"
                                                                                onClick={() => handleAddQueryToPlan(q.id, key)}
                                                                                className="flex w-full items-start gap-2 rounded-md border border-gray-100 px-3 py-2 text-left text-xs text-gray-700 transition-colors hover:border-primary-300 hover:bg-primary-50"
                                                                            >
                                                                                <Hash className="mt-0.5 h-3.5 w-3.5 shrink-0 text-gray-400" />
                                                                                <span className="min-w-0 flex-1 break-words">{q.name || `Query ${q.id}`}</span>
                                                                            </button>
                                                                        ))
                                                                    )}
                                                                </div>
                                                            </div>
                                                        );
                                                    })()}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </Card>

                    {/* Parameters Configuration — date params are excluded (managed by time frame) */}
                    {(() => {
                        // Filter out date parameters since time frames are system-managed
                        const nonDateParams = Object.fromEntries(
                            Object.entries(parameters_needed).map(([queryId, queryParams]) => [
                                queryId,
                                Object.fromEntries(
                                    Object.entries(queryParams).filter(([paramName, defaultValue]) =>
                                        !(String(paramName).includes('date') || String(defaultValue).match(/^\d{4}-\d{2}-\d{2}$/))
                                    )
                                )
                            ]).filter(([, queryParams]) => Object.keys(queryParams).length > 0)
                        );

                        if (Object.keys(nonDateParams).length === 0) return null;

                        return (
                            <Card padding="md" className="rounded-xl mb-6" title="Configure Parameters">
                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                                    {Object.entries(nonDateParams).map(([queryId, queryParams]) => (
                                        <div key={queryId} className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                                            <p className="font-medium text-gray-900 mb-3 flex items-center gap-2">
                                                <Hash className="w-4 h-4 text-gray-500" />
                                                {databaseQueriesMap[queryId]?.name || `Query ${queryId}`}
                                            </p>
                                            <div className="space-y-3">
                                                {Object.entries(queryParams).map(([paramName, defaultValue]) => {
                                                    const paramKey = `${queryId}_${paramName}`;
                                                    const isNumber = typeof defaultValue === 'number';
                                                    const rawVal = parameterValues[paramKey];
                                                    const inputValue =
                                                        rawVal === undefined || rawVal === null ? '' : rawVal;

                                                    return (
                                                        <div key={paramName}>
                                                            <label className="block text-xs font-medium text-gray-700 mb-1 capitalize">
                                                                {paramName.replace(/_/g, ' ')}
                                                            </label>
                                                            {isNumber ? (
                                                                <input
                                                                    type="number"
                                                                    value={inputValue}
                                                                    onChange={(e) => setParameterValues(prev => ({
                                                                        ...prev,
                                                                        [paramKey]: e.target.value
                                                                    }))}
                                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                                />
                                                            ) : (
                                                                <input
                                                                    type="text"
                                                                    value={inputValue}
                                                                    onChange={(e) => setParameterValues(prev => ({
                                                                        ...prev,
                                                                        [paramKey]: e.target.value
                                                                    }))}
                                                                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                                />
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </Card>
                        );
                    })()}

                    {/* Additional Notes — below Configure Parameters, above Recommendations.
                        Editable while the "Edit plan" toggle is on; read-only otherwise. */}
                    {planFullyEditable ? (() => {
                        const savedNotes = additional_notes || '';
                        const value = additionalNotesDraft ?? savedNotes;
                        const dirty = additionalNotesDraft !== null && additionalNotesDraft !== savedNotes;
                        const saving = planEditSaveTarget === 'notes';
                        return (
                            <Card padding="md" className="rounded-xl mb-6">
                                <h3 className="text-lg font-semibold text-gray-900 mb-3">Additional Notes</h3>
                                <AutoResizeTextarea
                                    value={value}
                                    onChange={(e) => setAdditionalNotesDraft(e.target.value)}
                                    disabled={updatingPlan}
                                    minHeight={96}
                                    placeholder="Add any notes about query selection, limits, or coverage…"
                                    className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-600 leading-relaxed focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50"
                                />
                                {dirty && (
                                    <div className="mt-2 flex items-center justify-end gap-2">
                                        <button
                                            type="button"
                                            onClick={() => setAdditionalNotesDraft(null)}
                                            disabled={updatingPlan}
                                            className="rounded-md px-2.5 py-1 text-xs font-medium text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="button"
                                            onClick={handleSaveAdditionalNotes}
                                            disabled={updatingPlan}
                                            className="inline-flex items-center gap-1.5 rounded-md bg-primary-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-primary-700 disabled:opacity-50"
                                        >
                                            {saving && <Loader className="h-3 w-3 animate-spin" />}
                                            {saving ? 'Saving…' : 'Save'}
                                        </button>
                                    </div>
                                )}
                            </Card>
                        );
                    })() : (additional_notes && (
                        <Card padding="md" className="rounded-xl mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-3">Additional Notes</h3>
                            <p className="text-sm text-gray-600 leading-relaxed">{additional_notes}</p>
                        </Card>
                    ))}

                    <ReportRecommendations
                        recommendations={recommendations}
                        databaseId={database?.id}
                        database={database}
                        reportGenerationId={reportData?.id || reportData?.report_generation_id}
                        databaseQueriesMap={databaseQueriesMap}
                        implementations={implementedRecommendations}
                        onImplementationsChange={handleImplementationsChange}
                    />

                    <ConfirmModal
                        isOpen={partialUpdateModalOpen}
                        onClose={() => setPartialUpdateModalOpen(false)}
                        onConfirm={runUpdatePlan}
                        title="Update plan with partial progress?"
                        message={
                            <>
                                <p className="mb-2">
                                    You completed {completedImplementations.length} of{' '}
                                    {recommendations.length} recommendations.
                                </p>
                                <p>
                                    Only finished items will be merged. Remaining gaps may still
                                    appear after the update.
                                </p>
                            </>
                        }
                        confirmText="Update plan"
                        confirmButtonVariant="primary"
                        loading={updatingPlan}
                        showWarning
                    />

                    {/* Query Detail Modal */}
                    {queryDetailModalInfo.isOpen && queryDetailModalInfo.queryId && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                            <div className="bg-white rounded-lg max-w-3xl w-full max-h-[90vh] overflow-y-auto custom-scrollbar">
                                <div className="p-6">
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                                                <Hash className="w-5 h-5 text-primary-600" />
                                            </div>
                                            <div>
                                                <h3 className="text-xl font-bold text-gray-900">
                                                    {databaseQueriesMap[queryDetailModalInfo.queryId]?.name || `Query ${queryDetailModalInfo.queryId}`}
                                                </h3>
                                                <p className="text-sm text-gray-500">Query Details</p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setQueryDetailModalInfo({ isOpen: false, queryId: null })}
                                            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                                        >
                                            <X className="w-5 h-5" />
                                        </button>
                                    </div>

                                    <div className="space-y-4">
                                        {databaseQueriesMap[queryDetailModalInfo.queryId]?.description && (
                                            <div>
                                                <h4 className="text-sm font-semibold text-gray-700 mb-1">Description</h4>
                                                <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-200">
                                                    {databaseQueriesMap[queryDetailModalInfo.queryId].description}
                                                </p>
                                            </div>
                                        )}
                                        <div>
                                            <h4 className="text-sm font-semibold text-gray-700 mb-1">SQL Query</h4>
                                            <pre className="text-sm text-gray-800 bg-gray-50 p-3 rounded-lg border border-gray-200 overflow-x-auto whitespace-pre-wrap">
                                                {databaseQueriesMap[queryDetailModalInfo.queryId]?.query ||
                                                    databaseQueriesMap[queryDetailModalInfo.queryId]?.query_text ||
                                                    databaseQueriesMap[queryDetailModalInfo.queryId]?.sql_query ||
                                                    'N/A'}
                                            </pre>
                                        </div>
                                        <div>
                                            <h4 className="text-sm font-semibold text-gray-700 mb-1">Input Parameters</h4>
                                            {(() => {
                                                const q = databaseQueriesMap[queryDetailModalInfo.queryId];
                                                const params = q?.parameters;
                                                if (!params) {
                                                    return (
                                                        <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-200">
                                                            None
                                                        </p>
                                                    );
                                                }

                                                // If it's an array of {name, ...}, render as a list; otherwise show JSON.
                                                if (Array.isArray(params)) {
                                                    if (params.length === 0) {
                                                        return (
                                                            <p className="text-sm text-gray-600 bg-gray-50 p-3 rounded-lg border border-gray-200">
                                                                None
                                                            </p>
                                                        );
                                                    }
                                                    return (
                                                        <div className="text-sm text-gray-800 bg-gray-50 p-3 rounded-lg border border-gray-200">
                                                            <ul className="list-disc pl-5 space-y-1">
                                                                {params.map((p, idx) => (
                                                                    <li key={p?.name || idx}>
                                                                        <span className="font-medium">{p?.name || `param_${idx + 1}`}</span>
                                                                        {p?.type ? <span className="text-gray-600"> ({p.type})</span> : null}
                                                                        {p?.required !== undefined ? (
                                                                            <span className="text-gray-600"> — {p.required ? 'required' : 'optional'}</span>
                                                                        ) : null}
                                                                        {p?.description ? <div className="text-gray-600">{p.description}</div> : null}
                                                                    </li>
                                                                ))}
                                                            </ul>
                                                        </div>
                                                    );
                                                }

                                                return (
                                                    <pre className="text-sm text-gray-800 bg-gray-50 p-3 rounded-lg border border-gray-200 overflow-x-auto whitespace-pre-wrap">
                                                        {JSON.stringify(params, null, 2)}
                                                    </pre>
                                                );
                                            })()}
                                        </div>
                                        {databaseQueriesMap[queryDetailModalInfo.queryId]?.validation_status && (
                                            <div>
                                                <h4 className="text-sm font-semibold text-gray-700 mb-1">Status</h4>
                                                <span className={`px-2.5 py-0.5 text-xs font-semibold rounded-md border inline-block ${databaseQueriesMap[queryDetailModalInfo.queryId].validation_status === 'validated'
                                                    ? 'bg-primary-50 text-primary-600 border-primary-200'
                                                    : databaseQueriesMap[queryDetailModalInfo.queryId].validation_status === 'failed'
                                                        ? 'bg-red-50 text-red-600 border-red-200'
                                                        : 'bg-yellow-50 text-yellow-600 border-yellow-200'
                                                    }`}>
                                                    {databaseQueriesMap[queryDetailModalInfo.queryId].validation_status}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const renderReportContent = () => {
        if (!selectedTimeFrame) {
            return (
                <div className="text-center py-12 text-gray-500">
                    <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg">Select a time frame to view the report</p>
                </div>
            );
        }

        // Check if this is an empty report (no queries were configured)
        const isEmptyReport = selectedTimeFrame.executed_queries &&
            selectedTimeFrame.executed_queries.length === 0 &&
            (selectedTimeFrame.report_content || '').includes('No queries configured');

        if (isEmptyReport) {
            return (
                <div className="flex flex-col items-center justify-center py-16">
                    <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <FileText className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
                    <p className="text-sm text-gray-500 text-center max-w-md">
                        No queries configured for this time period. Add queries to your insights to generate reports.
                    </p>
                </div>
            );
        }

        // Subtitle = the report's DATA date(s), not the generation date.
        const reportDate = (() => {
            const dr = selectedTimeFrame.date_range || {};
            const fmt = (s) => {
                const d = new Date(`${s}T00:00:00`);
                return Number.isNaN(d.getTime())
                    ? s
                    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
            };
            if (dr.start_date && dr.end_date && dr.start_date !== dr.end_date) {
                return `${fmt(dr.start_date)} – ${fmt(dr.end_date)}`;
            }
            if (dr.start_date) return fmt(dr.start_date);
            return selectedTimeFrame.created_at
                ? formatLocalDate(selectedTimeFrame.created_at).split(',')[0]
                : '';
        })();

        // Navigation functions for version history
        const goToPreviousVersion = () => {
            const allReports = selectedTimeFrame?.allReports || [];
            const currentIndex = allReports.findIndex(r => r.id === selectedTimeFrame?.id);
            if (currentIndex > 0) {
                setSelectedTimeFrame({ ...allReports[currentIndex - 1], allReports: allReports });
            }
        };

        const goToNextVersion = () => {
            const allReports = selectedTimeFrame?.allReports || [];
            const currentIndex = allReports.findIndex(r => r.id === selectedTimeFrame?.id);
            if (currentIndex < allReports.length - 1) {
                setSelectedTimeFrame({ ...allReports[currentIndex + 1], allReports: allReports });
            }
        };

        const allReports = selectedTimeFrame?.allReports || [];
        const currentIndex = allReports.findIndex(r => r.id === selectedTimeFrame?.id);
        const hasPrevious = currentIndex > 0;
        const hasNext = currentIndex < allReports.length - 1;
        const totalVersions = allReports.length;

        const getVersionLabel = (report) => {
            // Show the run's DATA date(s), not a "v1/v2" number or the generation time.
            const dr = report?.date_range || {};
            const fmt = (s) => {
                const d = new Date(`${s}T00:00:00`);
                return Number.isNaN(d.getTime())
                    ? s
                    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
            };
            let when = '';
            if (dr.start_date && dr.end_date && dr.start_date !== dr.end_date) {
                when = `${fmt(dr.start_date)} – ${fmt(dr.end_date)}`;
            } else if (dr.start_date) {
                when = fmt(dr.start_date);
            }
            return when;
        };

        const getPriorityBadge = (priority) => {
            switch (priority?.toUpperCase()) {
                case 'HIGH':
                    return {
                        bg: 'bg-red-50',
                        text: 'text-red-700',
                        border: 'border-red-100',
                        label: 'HIGH',
                    };
                case 'MEDIUM':
                    return {
                        bg: 'bg-amber-50',
                        text: 'text-amber-800',
                        border: 'border-amber-100',
                        label: 'MEDIUM',
                    };
                default:
                    return {
                        bg: 'bg-sky-50',
                        text: 'text-sky-800',
                        border: 'border-sky-100',
                        label: 'LOW',
                    };
            }
        };

        const formatSidebarStep = (step) => {
            if (!step) return '';
            const t = String(step).trim();
            if (/^step\s*\d+/i.test(t)) return t;
            return `Step 1: ${t}`;
        };

        return (
            <div className="flex gap-6">
                <div className="flex-1 min-w-0">
                    {/* Date Header */}
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <div className="flex items-center gap-2">
                                <h2 className="text-xl font-semibold text-gray-900">{(selectedTimeFrame.time_frame_label || '').replace(/\s*\(.*\)\s*$/, '') || selectedTimeFrame.time_frame_type}</h2>
                                {selectedTimeFrame.date_range_is_custom && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[11px] font-medium rounded-full bg-sky-50 text-sky-800 border border-sky-200">
                                        <Calendar className="w-3 h-3" />
                                        Custom dates
                                    </span>
                                )}
                            </div>
                            <p className="text-sm text-gray-500">{reportDate}</p>
                        </div>
                        <div className="flex items-center gap-2">
                            {/* Version Navigation — ◀ ▶ to step, or open the list to jump to any run */}
                            {totalVersions > 1 && (
                                <div className="flex items-center gap-1 mr-2 bg-gray-100 rounded-lg p-1">
                                    <button
                                        onClick={goToPreviousVersion}
                                        disabled={!hasPrevious}
                                        className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                                        title="Previous version"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                    </button>
                                    <div className="relative">
                                        <button
                                            onClick={() => setVersionsOpen((v) => !v)}
                                            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 rounded hover:bg-gray-200"
                                            title="Browse all runs of this time frame"
                                        >
                                            <Clock className="w-3 h-3" />
                                            <span>{getVersionLabel(selectedTimeFrame)}</span>
                                            <span className="text-gray-400">({totalVersions})</span>
                                            <ChevronDown className={`w-3 h-3 transition-transform ${versionsOpen ? 'rotate-180' : ''}`} />
                                        </button>
                                        {versionsOpen && (
                                            <>
                                                <div className="fixed inset-0 z-40" onClick={() => setVersionsOpen(false)} />
                                                <div className="absolute left-0 z-50 mt-2 w-72 max-h-96 overflow-auto rounded-xl border border-gray-200 bg-white p-1 shadow-xl">
                                                    {allReports.map((r) => {
                                                        const active = r.id === selectedTimeFrame?.id;
                                                        return (
                                                            <div
                                                                key={r.id}
                                                                className={`flex items-center gap-1 rounded-lg pr-1 transition-colors ${active ? 'bg-primary-50' : 'hover:bg-gray-50'}`}
                                                            >
                                                                <button
                                                                    onClick={() => {
                                                                        setSelectedTimeFrame({ ...r, allReports });
                                                                        setVersionsOpen(false);
                                                                    }}
                                                                    className="flex items-center gap-3 flex-1 min-w-0 rounded-lg px-3 py-2.5 text-left"
                                                                >
                                                                    <div className="flex-1 min-w-0 text-left">
                                                                        <p className={`text-sm font-medium ${active ? 'text-primary-700' : 'text-gray-900'}`}>
                                                                            {getVersionLabel(r) || 'Run'}
                                                                        </p>
                                                                        <p className="text-xs text-gray-500 mt-0.5 whitespace-nowrap">
                                                                            {r.created_at ? `Generated ${formatLocalDate(r.created_at)} · ${formatLocalTimeOnly(r.created_at)}` : ''}
                                                                        </p>
                                                                    </div>
                                                                    {active && <Check className="w-4 h-4 shrink-0 text-primary-600" />}
                                                                </button>
                                                                {allReports.length > 1 && (
                                                                    <button
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            setVersionsOpen(false);
                                                                            setDeleteVersionInfo({
                                                                                isOpen: true,
                                                                                timeFrameId: r.id,
                                                                                label: getVersionLabel(r) || 'this run',
                                                                                timeFrameType: r.time_frame_type || selectedTimeFrame?.time_frame_type || null,
                                                                            });
                                                                        }}
                                                                        className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 shrink-0"
                                                                        title="Delete this run"
                                                                    >
                                                                        <Trash2 className="w-3.5 h-3.5" />
                                                                    </button>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                    <button
                                        onClick={goToNextVersion}
                                        disabled={!hasNext}
                                        className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed"
                                        title="Next version"
                                    >
                                        <ChevronRight className="w-4 h-4" />
                                    </button>
                                </div>
                            )}
                            {(() => {
                                const byType = reportData?.time_frame_reports_by_type || {};
                                const runs = sortedTimeFrameReports(byType);
                                if (runs.length <= 1) return null;
                                const TYPE_NAMES = {
                                    yesterday: 'Yesterday',
                                    week_to_date: 'Week to Date',
                                    last_week: 'Last Week',
                                    month_to_date: 'Month to Date',
                                    last_month: 'Last Month',
                                    custom: 'Custom range',
                                };
                                const typeName = (t) =>
                                    TYPE_NAMES[t] || String(t || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                                const fmtRange = (dr = {}) => {
                                    const fmt = (s) => {
                                        const d = new Date(`${s}T00:00:00`);
                                        return Number.isNaN(d.getTime())
                                            ? s
                                            : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
                                    };
                                    if (dr.start_date && dr.end_date && dr.start_date !== dr.end_date) return `${fmt(dr.start_date)} – ${fmt(dr.end_date)}`;
                                    return dr.start_date ? fmt(dr.start_date) : '';
                                };
                                return (
                                    <div className="relative">
                                        <button
                                            onClick={() => setSavedRunsOpen((v) => !v)}
                                            className={`flex items-center gap-2 px-3.5 py-2 rounded-lg border text-sm transition-colors ${savedRunsOpen
                                                ? 'bg-primary-50 border-primary-300 text-primary-700'
                                                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                                            title="Browse previously generated reports"
                                        >
                                            <Archive className="w-4 h-4" />
                                            <span className="font-medium">{typeName(selectedTimeFrame?.time_frame_type)}</span>
                                            <ChevronDown className={`w-3.5 h-3.5 transition-transform ${savedRunsOpen ? 'rotate-180' : ''}`} />
                                        </button>
                                        {savedRunsOpen && (
                                            <>
                                                <div className="fixed inset-0 z-40" onClick={() => setSavedRunsOpen(false)} />
                                                <div className="absolute right-0 z-50 mt-2 w-80 max-h-96 overflow-auto rounded-xl border border-gray-200 bg-white p-1 shadow-xl">
                                                    {runs.map((run) => {
                                                        const rows = byType[run.time_frame_type] || [];
                                                        const active = run.time_frame_type === selectedTimeFrame?.time_frame_type;
                                                        const range = fmtRange(run.date_range);
                                                        return (
                                                            <button
                                                                key={run.id}
                                                                onClick={() => {
                                                                    const latest = pickLatestTimeFrameReport(rows) || run;
                                                                    setSelectedTimeFrame({ ...latest, allReports: rows });
                                                                    setSavedRunsOpen(false);
                                                                }}
                                                                className={`flex items-center gap-3 w-full rounded-lg px-3 py-2.5 text-left transition-colors ${active ? 'bg-primary-50' : 'hover:bg-gray-50'}`}
                                                            >
                                                                <div className="flex-1 min-w-0 text-left">
                                                                    <p className={`text-sm font-medium ${active ? 'text-primary-700' : 'text-gray-900'}`}>
                                                                        {typeName(run.time_frame_type)}
                                                                    </p>
                                                                    {range && <p className="text-xs text-gray-500 mt-0.5 whitespace-nowrap">{range}</p>}
                                                                </div>
                                                                {rows.length > 1 && (
                                                                    <span className="shrink-0 text-[11px] font-medium text-gray-500 bg-gray-100 rounded-full px-2 py-0.5">
                                                                        {rows.length} versions
                                                                    </span>
                                                                )}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                );
                            })()}
                            <DateRangePicker
                                startDate={selectedTimeFrame?.date_range?.start_date || dateDraft.start_date}
                                endDate={selectedTimeFrame?.date_range?.end_date || dateDraft.end_date}
                                onChange={(start, end) => setDateDraft({ start_date: start, end_date: end })}
                                onApply={handleApplyCustomDates}
                                disabled={syncing}
                            />
                            <button
                                onClick={() => setDateEditOpen((v) => !v)}
                                disabled={executing}
                                className={`flex items-center gap-2 px-3.5 py-2 rounded-lg border text-sm transition-colors disabled:opacity-50 ${dateEditOpen
                                    ? 'bg-primary-50 border-primary-300 text-primary-700'
                                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                                title="Anchor relative frames to a reference date"
                            >
                                <Clock className="w-4 h-4" />
                                Anchor
                                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${dateEditOpen ? 'rotate-180' : ''}`} />
                            </button>
                            <button
                                onClick={() => handleSyncReport(selectedTimeFrame.time_frame_type)}
                                disabled={syncing}
                                className="flex items-center gap-2 px-3.5 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm"
                            >
                                <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
                                {syncing ? 'Syncing…' : 'Sync'}
                            </button>
                        </div>
                    </div>

                    {dateEditOpen && (
                        <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                            {/* Anchor — recompute relative frames against a reference date */}
                            <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500">
                                <span>Anchor all frames to</span>
                                <input
                                    type="date"
                                    value={anchorDraft}
                                    onChange={(e) => setAnchorDraft(e.target.value)}
                                    className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-900 focus:border-primary-400 focus:ring-1 focus:ring-primary-300 outline-none"
                                />
                                <button
                                    onClick={() => handleReExecuteWithAnchor(anchorDraft)}
                                    disabled={executing}
                                    className="flex items-center gap-1.5 px-2.5 py-1 bg-white border border-gray-300 text-gray-600 rounded-md hover:bg-gray-50 disabled:opacity-50"
                                >
                                    <RefreshCw className={`w-3.5 h-3.5 ${executing ? 'animate-spin' : ''}`} />
                                    {executing ? 'Running…' : 'Re-run all'}
                                </button>
                                <span className="ml-auto text-gray-400">current: {reportData?.reference_date || 'today'}</span>
                            </div>
                        </div>
                    )}

                    {/* Generated dashboard: load live manifest from API; wait for Claude before showing tables */}
                    {liveExecutedQueries && liveExecutedQueries.length > 0 && (
                        <>
                            {dashboardReady ? (
                                <div className="mb-6" key={`generated-dash-${selectedTimeFrame.id}`}>
                                    <GeneratedReportDashboard
                                        topicKey={reportTopicNorm}
                                        manifest={generatedUiManifest ?? undefined}
                                        executedQueries={liveExecutedQueries}
                                        timeFrameLabel={selectedTimeFrame.time_frame_label}
                                        dateRange={selectedTimeFrame.date_range}
                                    />
                                </div>
                            ) : codegenStatus === 'failed' ? (
                                <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-900">
                                    <p className="font-medium">Report could not be created</p>
                                    <p className="mt-1 text-red-800">
                                        {codegenError ||
                                            'Claude Code ended with an error before the dashboard could be saved.'}
                                    </p>
                                </div>
                            ) : codegenStatus === 'skipped' ? (
                                <div className="mb-6 rounded-xl border border-amber-200 bg-amber-50 p-5 text-sm text-amber-900">
                                    <p className="font-medium">Report visualization skipped</p>
                                    <p className="mt-1">No query results were available to build a dashboard.</p>
                                </div>
                            ) : (
                                <div className="mb-6 flex flex-col items-center justify-center rounded-2xl border border-primary-100 bg-gradient-to-b from-primary-50/80 to-white px-8 py-16 text-center">
                                    <Loader className="mb-4 h-10 w-10 animate-spin text-primary-600" />
                                    <p className="text-base font-semibold text-gray-900">Building report dashboard</p>
                                    <p className="mt-2 max-w-md text-sm text-gray-600">
                                        Claude Code is generating your visualization. Charts and query details will
                                        load here when ready.
                                    </p>
                                </div>
                            )}
                        </>
                    )}

                    {liveExecutedQueries && liveExecutedQueries.length > 0 && showExecutedQueriesPanel && (
                        <div className="mt-6">
                            <button
                                type="button"
                                onClick={() => setExecutedQueriesExpanded((prev) => !prev)}
                                className="section-card-toggle flex w-full items-start justify-between gap-3 rounded-lg text-left transition-colors hover:bg-gray-50/80 -mx-2 px-2 py-1"
                                aria-expanded={executedQueriesExpanded}
                            >
                                <div>
                                    <h3 className="text-lg font-semibold text-gray-900">Executed Queries</h3>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Previewing up to 10 rows for each query result.
                                    </p>
                                </div>
                                {executedQueriesExpanded ? (
                                    <ChevronUp className="mt-1 h-5 w-5 shrink-0 text-gray-500" aria-hidden />
                                ) : (
                                    <ChevronDown className="mt-1 h-5 w-5 shrink-0 text-gray-500" aria-hidden />
                                )}
                            </button>
                            {executedQueriesExpanded && (
                                <div className="mt-5 space-y-5">
                                    {liveExecutedQueries.map((query, idx) => (
                                        <ExecutedQueryPreviewCard
                                            key={query?.id || query?.query_id || `${query?.query_name || 'query'}-${idx}`}
                                            query={query}
                                            index={idx}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="w-80 flex-shrink-0 self-start">
                        <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                            <div className="flex items-center justify-between gap-2 mb-4">
                                <h3 className="text-sm font-semibold text-gray-900">Recommended Priorities</h3>
                                {plans.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setIncludeArchivedPlans(!includeArchivedPlans)}
                                        className={`inline-flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-md border transition-colors ${includeArchivedPlans
                                            ? 'bg-white text-gray-800 border-gray-300 shadow-sm'
                                            : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                            }`}
                                        title={includeArchivedPlans ? 'Show active plans only' : 'Include archived plans'}
                                    >
                                        {includeArchivedPlans ? 'All' : 'Active'}
                                        <ChevronDown className="w-3.5 h-3.5 text-gray-500" aria-hidden />
                                    </button>
                                )}
                            </div>
                            <button
                                type="button"
                                onClick={() => handleGeneratePriorities(selectedTimeFrame.time_frame_type)}
                                disabled={generatingPlans}
                                className="w-full flex items-center justify-center gap-2 px-3 py-2 mb-4 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm"
                            >
                                {generatingPlans ? (
                                    <Loader className="w-4 h-4 animate-spin" />
                                ) : (
                                    <Sparkles className="w-4 h-4" />
                                )}
                                {generatingPlans
                                    ? 'Generating…'
                                    : plans.length > 0
                                        ? 'Regenerate priorities'
                                        : 'Generate priorities'}
                            </button>
                            {plans.length > 0 ? (
                            <div className="flex flex-col gap-4">
                                {plans.map((plan) => {
                                    const priorityBadge = getPriorityBadge(plan.priority);
                                    const stepPreview = formatSidebarStep(plan.steps?.[0]);
                                    return (
                                        <Card
                                            key={plan.id}
                                            padding="sm"
                                            className="shadow-sm"
                                        >
                                            <div className="flex items-start gap-2">
                                                <div
                                                    role="button"
                                                    tabIndex={0}
                                                    onClick={() =>
                                                        navigate(
                                                            `/reports/${databaseId}/plan?planId=${plan.id}&reportId=${reportData.id}&timeFrameType=${selectedTimeFrame.time_frame_type}`,
                                                        )
                                                    }
                                                    onKeyDown={(e) => {
                                                        if (e.key === 'Enter' || e.key === ' ') {
                                                            e.preventDefault();
                                                            navigate(
                                                                `/reports/${databaseId}/plan?planId=${plan.id}&reportId=${reportData.id}&timeFrameType=${selectedTimeFrame.time_frame_type}`,
                                                            );
                                                        }
                                                    }}
                                                    className="flex-1 min-w-0 text-left cursor-pointer rounded-md hover:bg-gray-50/80 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary-500/30"
                                                >
                                                    <h4 className="text-sm font-semibold text-gray-900 leading-snug">
                                                        {plan.topic}
                                                    </h4>
                                                    {stepPreview && (
                                                        <p className="text-xs text-gray-600 leading-relaxed mt-2 line-clamp-3">
                                                            {stepPreview}
                                                        </p>
                                                    )}
                                                </div>
                                                <div className="flex flex-col items-end gap-2 shrink-0">
                                                    {plan.priority && (
                                                        <span
                                                            className={`px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide rounded-full border ${priorityBadge.bg} ${priorityBadge.text} ${priorityBadge.border}`}
                                                        >
                                                            {priorityBadge.label}
                                                        </span>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() =>
                                                            handleSetPlanStatus(
                                                                plan.id,
                                                                plan.status === 'archived' ? 'active' : 'archived',
                                                            )
                                                        }
                                                        className="report-card-action inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                                                    >
                                                        <Archive className="w-3.5 h-3.5 text-gray-500" />
                                                        {plan.status === 'archived' ? 'Unarchive' : 'Archive'}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap items-center gap-1.5 mt-2">
                                                {plan.status === 'superseded' && (
                                                    <span className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[11px] font-medium rounded-full bg-amber-50 text-amber-900 border border-amber-200">
                                                        <GitBranch className="w-3 h-3" />
                                                        Snapshot
                                                    </span>
                                                )}
                                                {plan.status === 'archived' && (
                                                    <span className="px-2 py-0.5 text-[11px] font-medium rounded-full bg-gray-100 text-gray-700 border border-gray-200">
                                                        Archived
                                                    </span>
                                                )}
                                                {plan.supersedes_plan_id && plan.status !== 'superseded' && (
                                                    <span className="text-[11px] text-sky-700" title="Linked to prior version after sync">
                                                        From #{plan.supersedes_plan_id}
                                                    </span>
                                                )}
                                            </div>
                                            {plan.continued_by_plan_id ? (
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        navigate(
                                                            `/reports/${databaseId}/plan?planId=${plan.continued_by_plan_id}&reportId=${reportData.id}&timeFrameType=${selectedTimeFrame.time_frame_type}`,
                                                        )
                                                    }
                                                    className="mt-2 text-xs font-semibold text-primary-600 hover:text-primary-800"
                                                >
                                                    Open current plan →
                                                </button>
                                            ) : null}
                                            <PlanUsedSkillsTags skills={plan.skills} className="mt-3 px-0" />
                                        </Card>
                                    );
                                })}
                            </div>
                            ) : generatingPlans ? (
                                <p className="text-xs text-gray-500 text-center py-2">
                                    Analyzing the data and writing priorities… this can take a moment.
                                </p>
                            ) : (
                                <p className="text-xs text-gray-500 text-center py-2">
                                    No priorities yet for this time frame. Click Generate priorities to analyze the data.
                                </p>
                            )}
                        </div>
                    </div>
            </div>
        );
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white border-b border-gray-200 py-4 px-page">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => {
                                if (inSettings) {
                                    setInSettings(false);
                                    setShowMasterPlan(false);
                                    setShowMasterPlanDetails(false);
                                    setViewingReport(false);
                                    setSelectedTimeFrame(null);
                                    navigate(`/reports/${databaseId}`, { replace: true });
                                } else if (showMasterPlan) {
                                    setShowMasterPlan(false);
                                    setShowMasterPlanDetails(false);
                                } else if (viewingReport) {
                                    setViewingReport(false);
                                    setSelectedTimeFrame(null);
                                    navigate(`/reports/${databaseId}`, { replace: true });
                                } else {
                                    navigate('/reports');
                                }
                            }}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <ArrowLeft className="w-5 h-5" />
                        </button>
                        <div>
                            <h1 className="text-lg font-semibold text-gray-900">
                                {inSettings
                                    ? 'Report settings'
                                    : (viewingReport || showMasterPlan) && reportData?.topic
                                        ? reportData.topic
                                        : 'Report Generation'}
                            </h1>
                            {inSettings ? (
                                <p className="text-sm text-gray-500">
                                    Edit the report name below, then configure parameters
                                </p>
                            ) : viewingReport ? (
                                <p className="text-sm text-gray-500">
                                    {database?.name || 'Synced query results and dashboards'}
                                </p>
                            ) : showMasterPlan ? (
                                <p className="text-sm text-gray-500">
                                    Review the AI-generated plan and configure parameters before execution
                                </p>
                            ) : (
                                <p className="text-sm text-gray-500">
                                    Performance overview across all completed reports — open a report below for its topic dashboard
                                </p>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        {!viewingReport ? (
                            <>
                                <button
                                    type="button"
                                    onClick={() => navigate(`/reports/${databaseId}/priorities`)}
                                    className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                                >
                                    <Lightbulb className="w-4 h-4 text-amber-500" />
                                    Recommended priorities
                                </button>
                                <button
                                    onClick={() => {
                                        setReportDescription('');
                                        setShowCreateModal(true);
                                    }}
                                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
                                >
                                    <Plus className="w-4 h-4" />
                                    Create New Report
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    onClick={() => navigate(`/reports/${databaseId}/priorities`)}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                                >
                                    <Lightbulb className="w-4 h-4 text-amber-500" />
                                    Priorities
                                </button>
                                <button
                                    type="button"
                                    onClick={goToOverallPerformanceLanding}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-200 bg-white text-gray-700 hover:bg-gray-50"
                                >
                                    <LayoutDashboard className="w-4 h-4 text-gray-500" />
                                    All reports
                                </button>
                            </>
                        )}
                        <div className="h-8 w-px bg-gray-200 mx-1 hidden sm:block"></div>
                        <button
                            onClick={() => setShowRightSidebar(prev => !prev)}
                            className={`p-2 rounded-lg transition-colors border ${showRightSidebar ? 'bg-primary-50 text-primary-600 border-primary-200' : 'bg-white text-gray-500 border-gray-200 hover:bg-gray-50'}`}
                            title={showRightSidebar ? "Hide Sidebar" : "Show Sidebar"}
                        >
                            <PanelRight className="w-5 h-5" />
                        </button>
                    </div>
                </div>
            </div>

            <div className="flex">
                {/* Main Content */}
                <div className="flex-1 min-w-0 p-6">
                    {/* Overall Performance Dashboard (Claude-generated) - shown when not viewing a report */}
                    {!viewingReport && !showMasterPlan && (
                        <div className="page-content w-full">
                            <div className="flex items-center justify-between mb-4">
                                <div>
                                    <h2 className="text-lg font-semibold text-gray-900">
                                        {database?.name ? `${database.name} · Overall Performance` : 'Overall Performance'}
                                    </h2>
                                    <p className="text-xs text-gray-500 mt-1">
                                        Combined view across every report and time frame for this database.
                                    </p>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => navigate(`/reports/${databaseId}/priorities`)}
                                    className="inline-flex items-center gap-2 rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700"
                                >
                                    <Lightbulb className="w-4 h-4" />
                                    Recommended Priorities
                                </button>
                            </div>

                            {overallDashboardReady && !overallLoading && overallReports.length > 0 ? (
                                <div>
                                    <GeneratedOverallDashboard
                                        databaseId={databaseId}
                                        manifest={generatedUiManifest ?? undefined}
                                        reports={overallReports}
                                        databaseName={database?.name}
                                        onOpenPriorities={() => navigate(`/reports/${databaseId}/priorities`)}
                                    />
                                </div>
                            ) : overallStatus.status === 'failed' ? (
                                <div className="rounded-2xl border border-red-200 bg-white px-8 py-16 text-center">
                                    <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
                                    <p className="text-base font-semibold text-gray-900">Dashboard generation failed</p>
                                    <p className="text-sm text-gray-500 mt-2 max-w-md mx-auto">
                                        {overallStatus.error || 'Something went wrong while building the overall dashboard.'}
                                    </p>
                                    {hasAnyCompletedReport && (
                                        <p className="text-sm text-gray-500 mt-4 max-w-md mx-auto">
                                            You can still view individual reports from the right sidebar.
                                        </p>
                                    )}
                                </div>
                            ) : overallLoading ? (
                                <div className="flex items-center justify-center rounded-2xl border border-gray-200 bg-white px-8 py-16 text-sm text-gray-600">
                                    <Loader className="w-5 h-5 animate-spin mr-2 text-primary-600" />
                                    Loading reports…
                                </div>
                            ) : !hasAnyCompletedReport ? (
                                <div className="rounded-2xl border border-gray-200 bg-white px-8 py-16 text-center">
                                    <FileText className="w-10 h-10 text-gray-300 mx-auto mb-4" />
                                    <p className="text-base font-semibold text-gray-900">
                                        Create a report to make dashboard report
                                    </p>
                                </div>
                            ) : overallDashboardReady && overallReports.length === 0 ? (
                                <div className="flex items-center justify-center rounded-2xl border border-gray-200 bg-white px-8 py-16 text-sm text-gray-600">
                                    <Loader className="w-5 h-5 animate-spin mr-2 text-primary-600" />
                                    Preparing dashboard data…
                                </div>
                            ) : (
                                <div className="flex flex-col items-center justify-center rounded-2xl border border-primary-100 bg-gradient-to-b from-primary-50/80 to-white px-8 py-16 text-center">
                                    <Loader className="mb-4 h-10 w-10 animate-spin text-primary-600" />
                                    <p className="text-base font-semibold text-gray-900">Building overall dashboard</p>
                                    <p className="mt-2 max-w-md text-sm text-gray-600">
                                        Claude Code is generating your visualization. It will appear here when ready.
                                    </p>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Master Plan Details View - Full Page */}
                    {!viewingReport && showMasterPlan && showMasterPlanDetails && masterPlanData && (
                        <div className="p-0">
                            {renderMasterPlanDetails()}
                        </div>
                    )}

                    {/* Viewing Report */}
                    {viewingReport && selectedTimeFrame && (
                        <div className="relative">
                            {renderReportContent()}
                        </div>
                    )}

                    {/* Completed Report View - date-range prompt + existing report list */}
                    {viewingReport && reportData && reportData.status === 'completed' && !selectedTimeFrame && (
                        <div className="max-w-2xl mx-auto py-8">
                            <div className="rounded-2xl border border-primary-100 bg-gradient-to-b from-primary-50/70 to-white px-8 py-10 text-center">
                                <Calendar className="w-10 h-10 text-primary-500 mx-auto mb-4" />
                                <h3 className="text-lg font-semibold text-gray-900">Select a date range to view results</h3>
                                <p className="mt-2 text-sm text-gray-600">
                                    Pick a range from the date picker above, then Apply to run the report for that period.
                                </p>
                            </div>

                            {(() => {
                                const existing = sortedTimeFrameReports(reportData.time_frame_reports_by_type);
                                if (!existing.length) return null;
                                return (
                                    <div className="mt-8">
                                        <h4 className="text-sm font-semibold text-gray-900 mb-3">Existing reports</h4>
                                        <div className="flex flex-col gap-2">
                                            {existing.map((report) => {
                                                const tfType = report.time_frame_type;
                                                const allForType = reportData.time_frame_reports_by_type[tfType] || [];
                                                const dr = report.date_range || {};
                                                const fmt = (s) => {
                                                    const d = new Date(`${s}T00:00:00`);
                                                    return Number.isNaN(d.getTime())
                                                        ? s
                                                        : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
                                                };
                                                const rangeLabel = dr.start_date && dr.end_date && dr.start_date !== dr.end_date
                                                    ? `${fmt(dr.start_date)} – ${fmt(dr.end_date)}`
                                                    : dr.start_date ? fmt(dr.start_date) : '';
                                                return (
                                                    <button
                                                        key={report.id}
                                                        type="button"
                                                        onClick={() => {
                                                            const latest = pickLatestTimeFrameReport(allForType) || report;
                                                            setSelectedTimeFrame({ ...latest, allReports: allForType });
                                                            setViewingReport(true);
                                                            setShowRightSidebar(false);
                                                        }}
                                                        className="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 py-3 text-left hover:border-primary-300 hover:bg-primary-50/40 transition-colors"
                                                    >
                                                        <div className="min-w-0">
                                                            <p className="text-sm font-medium text-gray-900 truncate">{report.time_frame_label || tfType}</p>
                                                            {rangeLabel && <p className="text-xs text-gray-500 mt-0.5">{rangeLabel}</p>}
                                                        </div>
                                                        <span className="flex items-center gap-2 shrink-0 text-xs text-gray-400">
                                                            {allForType.length > 1 && <span>{allForType.length} versions</span>}
                                                            <ArrowRight className="w-4 h-4" />
                                                        </span>
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })()}
                        </div>
                    )}

                    {/* Failed Status */}
                    {viewingReport && reportData && reportData.status === 'failed' && (
                        <div>
                            <div className="max-w-lg mx-auto">
                                <div className="bg-white rounded-xl border border-gray-200 text-center">
                                    <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Report unavailable</h3>
                                    <p className="text-sm text-gray-500">
                                        Something went wrong while building this report. Try syncing again or open settings to review the plan.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Right Sidebar - Combined Recommended Priorities & Previous Reports */}
                <div
                    className={`bg-white border-l border-gray-200 overflow-y-auto overflow-x-hidden transition-all duration-300 ease-in-out flex-shrink-0 z-10 ${showRightSidebar ? 'w-80 opacity-100' : 'w-0 opacity-0 border-none'
                        }`}
                >
                    <div className={`w-80 p-3 transition-opacity duration-300 ${showRightSidebar ? 'opacity-100' : 'opacity-0'}`}>

                        <h3 className="text-sm font-semibold text-gray-900 mb-4">
                            Previous Reports (
                            {reportsList.length > 0 ? reportsList.length + 1 : 0}
                            )
                        </h3>
                        <div className="flex flex-col gap-4">
                            {reportsList.length > 0 && (
                                <Card
                                    padding="sm"
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            goToOverallPerformanceLanding();
                                        }
                                    }}
                                    className={`cursor-pointer transition-all hover:border-gray-300 ${!viewingReport && !showMasterPlan && !inSettings
                                        ? 'border-primary-300 ring-1 ring-primary-100 shadow-sm'
                                        : 'border-gray-200'
                                        }`}
                                    onClick={goToOverallPerformanceLanding}
                                >
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span className="px-2 py-0.5 bg-primary-50 text-primary-800 rounded text-xs shrink-0 leading-none">
                                            Overview
                                        </span>
                                        <p className="font-medium text-gray-900 text-sm truncate flex items-center gap-1.5 min-w-0 leading-tight">
                                            <LayoutDashboard className="w-3.5 h-3.5 shrink-0 text-primary-600" />
                                            Overall Report
                                        </p>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-3 leading-none">Overall Performance</p>
                                </Card>
                            )}
                            {[...reportsList]
                                .sort((a, b) => (a.status === 'completed' ? 0 : 1) - (b.status === 'completed' ? 0 : 1))
                                .map((report) => (
                                <Card
                                    key={report.id}
                                    padding="sm"
                                    className="cursor-pointer transition-all hover:border-gray-300 border-gray-200"
                                    onClick={() => {
                                        fetchReportDetails(report.id);
                                    }}
                                >
                                    <div className="flex items-center gap-2">
                                        <div className="flex items-center gap-2 min-w-0 flex-1">
                                            {report.status === 'completed' ? (
                                                <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded text-xs shrink-0 leading-none">Completed</span>
                                            ) : report.status === 'failed' ? (
                                                <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded text-xs shrink-0 leading-none">Failed</span>
                                            ) : (
                                                <Loader className="w-3.5 h-3.5 text-yellow-600 animate-spin shrink-0" />
                                            )}
                                            <p className="font-medium text-gray-900 text-sm truncate leading-tight">{report.topic}</p>
                                        </div>
                                        <div className="flex items-center gap-0.5 shrink-0">
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setInSettings(true);
                                                    setViewingReport(false);
                                                    setSelectedTimeFrame(null);
                                                    fetchReportDetails(report.id, null, (data) => {
                                                        if (data?.master_plan) {
                                                            setMasterPlanData(data.master_plan);
                                                            setShowMasterPlan(true);
                                                            setShowMasterPlanDetails(true);
                                                        }
                                                    }, true);
                                                }}
                                                className="report-card-action text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                                                title="Settings"
                                            >
                                                <Settings className="w-3.5 h-3.5" />
                                            </button>
                                            <button
                                                type="button"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setDeleteModalInfo({ isOpen: true, id: report.id });
                                                }}
                                                className="report-card-action text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                                                title="Delete Report"
                                            >
                                                <Trash2 className="w-3.5 h-3.5" />
                                            </button>
                                        </div>
                                    </div>
                                    <p className="text-xs text-gray-500 mt-3 leading-none">
                                        {report.created_at ? formatLocalDate(report.created_at).split(',')[0] : ''}
                                    </p>
                                </Card>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteModalInfo.isOpen}
                onClose={() => setDeleteModalInfo({ isOpen: false, id: null })}
                onConfirm={handleDeleteReport}
                loading={deletingReport}
                title="Confirm Delete"
                message="Are you sure you want to delete this report? This action cannot be undone."
                confirmText="Delete"
                cancelText="Cancel"
            />

            <ConfirmModal
                isOpen={deleteVersionInfo.isOpen}
                onClose={() => setDeleteVersionInfo({ isOpen: false, timeFrameId: null, label: '', timeFrameType: null })}
                onConfirm={handleDeleteTimeFrameVersion}
                loading={deletingVersion}
                title="Delete this run?"
                message={`Delete the "${deleteVersionInfo.label}" run of this time frame? Its report and any generated priorities are removed. This cannot be undone.`}
                confirmText="Delete"
                cancelText="Cancel"
                showWarning
            />

            {/* Create Report Modal - Full Screen */}
            {showCreateModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => !loading && setShowCreateModal(false)}>
                    <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-bold text-gray-900">Create A New Report</h2>
                                <button
                                    onClick={() => setShowCreateModal(false)}
                                    disabled={loading}
                                    className={`text-gray-400 hover:text-gray-600 ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <X className="w-5 h-5" />
                                </button>
                            </div>

                            {/* Topic Selection */}
                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-3">
                                    Choose Topic *
                                </label>
                                <div className="flex flex-wrap gap-2 mb-4">
                                    {topicPresets.map((preset) => (
                                        <button
                                            key={preset}
                                            onClick={() => setTopic(preset)}
                                            className={`px-3 py-2 text-sm rounded-lg border transition-all ${topic === preset
                                                ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                                                : 'bg-white text-gray-700 border-gray-300 hover:border-primary-400 hover:bg-primary-50'
                                                }`}
                                        >
                                            {preset}
                                        </button>
                                    ))}
                                </div>
                                <div className="mb-3">
                                    <input
                                        type="text"
                                        value={topic}
                                        onChange={(e) => setTopic(e.target.value)}
                                        placeholder="Or Manually Input Topic"
                                        className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                                    />
                                </div>
                                <p className="text-xs text-gray-500 mt-2">
                                    AI will analyze and structure your topic automatically
                                </p>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-gray-700 mb-2" htmlFor="report-description">
                                    Report description <span className="text-gray-400 font-normal">(optional)</span>
                                </label>
                                <textarea
                                    id="report-description"
                                    ref={descriptionRef}
                                    value={reportDescription}
                                    onChange={(e) => setReportDescription(e.target.value)}
                                    placeholder="What should this report cover? Sections to include, comparisons, audience, metrics to emphasize, or things to avoid."
                                    rows={1}
                                    className="w-full px-4 py-2.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all resize-none min-h-[96px] overflow-hidden"
                                />

                            </div>

                            {/* Create Button */}
                            <button
                                onClick={() => {
                                    if (!topic.trim()) {
                                        showToast('Please select or enter a topic', 'error');
                                        return;
                                    }
                                    handleCreateReport();
                                }}
                                disabled={loading || !topic.trim()}
                                className="w-full py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium transition-all shadow-sm hover:shadow-md"
                            >
                                {loading ? (
                                    <>
                                        <Loader className="w-4 h-4 animate-spin" />
                                        Creating Report Generation Plan...
                                    </>
                                ) : (
                                    <>
                                        <Zap className="w-4 h-4" />
                                        Create Report Generation Plan
                                        <ArrowRight className="w-4 h-4" />
                                    </>
                                )}
                            </button>

                            {/* Footer Badges */}
                            <div className="flex items-center justify-center gap-6 mt-5 pt-4 border-t border-gray-100 text-xs text-gray-500">
                                <span className="flex items-center gap-1.5">
                                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                                    AI Ready
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <Shield className="w-3 h-3 text-primary-500" />
                                    Secure Processing
                                </span>
                                <span className="flex items-center gap-1.5">
                                    <div className="w-2 h-2 bg-primary-500 rounded-full"></div>
                                    ~2-3 minutes
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <button
                onClick={() => {
                    const target = `/reports/${databaseId}/chat`;
                    // Pre-select the report currently being viewed; dropdown still lets the
                    // user switch. Falls back to the chat's own default selection otherwise.
                    if (viewingReport && reportTopicNorm) {
                        navigate(`${target}?selected=topic:${encodeURIComponent(reportTopicNorm)}`);
                    } else {
                        navigate(target);
                    }
                }}
                aria-label="Open Report Chat"
                title="Report Chat"
                type="button"
                className="group fixed bottom-6 right-6 z-30 inline-flex items-center gap-2 pl-4 pr-5 h-14 rounded-full bg-gradient-to-br from-primary-500 to-primary-700 text-white shadow-xl shadow-primary-400/40 ring-4 ring-white hover:shadow-2xl hover:shadow-primary-500/40 hover:-translate-y-0.5 transition-all"
            >
                <span className="relative flex items-center justify-center w-8 h-8 rounded-full bg-white/15">
                    <MessageCircle className="w-5 h-5" />
                    <span className="absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-emerald-400 ring-2 ring-white" />
                </span>
                <span className="text-sm font-semibold tracking-tight">Report Chat</span>
            </button>
        </div>
    );
};

export default ReportGeneration;
